import {
  AssessmentOutlined,
  AutoAwesomeOutlined,
  BugReportOutlined,
  CalendarMonthOutlined,
  Check,
  Close,
  DescriptionOutlined,
  ExpandLess,
  ExpandMore,
  FolderOpenOutlined,
  InfoOutlined,
  KeyboardArrowRight,
  LightbulbOutlined,
  Search,
  ThumbDownOutlined,
  ThumbUpOutlined
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import FluentDonutChart from "../../../ChartComponents/FluentDountChart";
import {
  ConsolidatedWorkItem,
  CustomerFeedbackSnapshotResponse,
  ProductInsightDataStatus
} from "../../../interfaces/CloseTheLoop.interfaces";
import {
  useGetCustomerEngagementDataQuery,
  useGetUnifiedInsightsProductEngagementDataQuery,
  useGetUnifiedInsightsTopCustomerNeedsDrilldownDataQuery,
  useGetUnifiedInsightsUsageMetricsQuery
} from "../../../slices/CloseTheLoop.slice";
import CompetitiveAssessmentCard from "./CompetitiveAssessmentCard";
import ExecutiveSummarySections, { SummaryBullet } from "./ExecutiveSummarySections";
import { AssessmentTheme, selectCompetitiveAssessment } from "./competitiveAssessmentUtils";
import UXResearchInsightsCard from "./UXResearchInsightsCard";
import UsageMetricsCards from "./UsageMetricsCards";
import { insightCardTitleStyles, insightCardToggleStyles } from "./insightCardStyles";
import { themePillarStyles as needPillarStyles } from "./ThemePillarHeader";

const metricCards = [
  { label: "ADO Bugs", color: "#4F7DFF", icon: BugReportOutlined },
  { label: "DCRs", color: "#EC008C", icon: DescriptionOutlined },
  { label: "Feature requests", color: "#16B8C4", icon: LightbulbOutlined },
  { label: "IcMs", color: "#9B72CF", icon: FolderOpenOutlined },
  { label: "Survey Responses", color: "#16C60C", icon: AssessmentOutlined }
];

const palette = {
  navy: "#091F3A",
  blue: "#0F6CBD",
  cyan: "#19A7CE",
  teal: "#0F7B6C",
  green: "#107C10",
  amber: "#F2C811",
  orange: "#D83B01",
  red: "#C50F1F",
  purple: "#8764B8",
  ink: "#242424",
  muted: "#616161",
  line: "#E0E0E0",
  canvas: "#F5F7FA"
};

const surface = {
  border: `1px solid ${palette.line}`,
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 1px 2px rgba(0,0,0,.08), 0 8px 24px rgba(15,45,75,.06)"
};

const timeframeOrder = ["AllUp", "3 Months", "6 Months", "12 Months"];

type CustomerColumnFilters = {
  customer: string;
  product: string;
  country: string;
  region: string;
  segment: string;
  featureRequests: string;
  featureStatuses: string;
  icms: string;
  icmStatuses: string;
  dcrs: string;
  dcrStatuses: string;
  adoBugs: string;
  adoBugStatuses: string;
  engagementSummary: string;
};

type CustomerSortKey = keyof CustomerColumnFilters;

const customerTableBaseColumns: { label: string; filterKey: CustomerSortKey }[] = [
  { label: "Customer", filterKey: "customer" },
  { label: "Product", filterKey: "product" },
  { label: "Country", filterKey: "country" },
  { label: "Region", filterKey: "region" },
  { label: "Segment", filterKey: "segment" }
];

const customerTableFeatureColumns: { label: string; filterKey: CustomerSortKey }[] = [
  { label: "Feature requests", filterKey: "featureRequests" },
  { label: "Feature statuses", filterKey: "featureStatuses" },
  { label: "IcMs", filterKey: "icms" },
  { label: "IcM statuses", filterKey: "icmStatuses" }
];

const customerTablePurviewColumns: { label: string; filterKey: CustomerSortKey }[] = [
  { label: "DCRs", filterKey: "dcrs" },
  { label: "DCR statuses", filterKey: "dcrStatuses" },
  { label: "ADO Bugs", filterKey: "adoBugs" },
  { label: "ADO Bug statuses", filterKey: "adoBugStatuses" }
];

const customerTableSummaryColumn: { label: string; filterKey: CustomerSortKey } = {
  label: "Engagement summary",
  filterKey: "engagementSummary"
};

const initialCustomerColumnFilters: CustomerColumnFilters = {
  customer: "",
  product: "",
  country: "",
  region: "",
  segment: "",
  featureRequests: "",
  featureStatuses: "",
  icms: "",
  icmStatuses: "",
  dcrs: "",
  dcrStatuses: "",
  adoBugs: "",
  adoBugStatuses: "",
  engagementSummary: ""
};

type ContributingFeedbackSortKey =
  | "feedbackId"
  | "feedbackTypeLabel"
  | "feedbackTitle"
  | "customerCount"
  | "customers"
  | "feedbackDetails"
  | "feedbackStatus";
type ContributingFeedbackColumnFilters = Record<ContributingFeedbackSortKey, string>;

const contributingFeedbackColumns: { label: string; sortKey: ContributingFeedbackSortKey }[] = [
  { label: "Feedback ID", sortKey: "feedbackId" },
  { label: "Type", sortKey: "feedbackTypeLabel" },
  { label: "Title", sortKey: "feedbackTitle" },
  { label: "Details", sortKey: "feedbackDetails" },
  { label: "Customer count", sortKey: "customerCount" },
  { label: "Customers", sortKey: "customers" },
  { label: "Status", sortKey: "feedbackStatus" }
];

const initialContributingFeedbackColumnFilters: ContributingFeedbackColumnFilters = {
  feedbackId: "",
  feedbackTypeLabel: "",
  feedbackTitle: "",
  customerCount: "",
  customers: "",
  feedbackDetails: "",
  feedbackStatus: ""
};

type ConsolidatedWorkItemParseResult = {
  items: ConsolidatedWorkItem[];
  error: string | null;
};

const isConsolidatedWorkItem = (value: unknown): value is ConsolidatedWorkItem => {
  if (!value || typeof value !== "object") return false;

  const item = value as Record<string, unknown>;
  return [
    "Id",
    "Source",
    "Title",
    "Description",
    "Status",
    "Priority",
    "Severity",
    "TriageStatus",
    "CreatedDate",
    "UpdatedDate",
    "URL"
  ].every((key) => typeof item[key] === "string");
};

const parseConsolidatedWorkItems = (value: string | null): ConsolidatedWorkItemParseResult => {
  if (!value?.trim()) return { items: [], error: null };

  try {
    const parsedValue: unknown = JSON.parse(value);

    if (!Array.isArray(parsedValue) || !parsedValue.every(isConsolidatedWorkItem)) {
      return {
        items: [],
        error: "Consolidated work items could not be displayed because the response format is invalid."
      };
    }

    return { items: parsedValue, error: null };
  } catch (error) {
    return {
      items: [],
      error:
        error instanceof SyntaxError
          ? "Consolidated work items could not be displayed because the response contains invalid JSON."
          : "Consolidated work items could not be displayed due to an unexpected parsing error."
    };
  }
};

const getWorkItemGroupKey = (source: string) => {
  const normalizedSource = source.trim().toLowerCase();
  if (/^features?( requests?)?$/.test(normalizedSource)) return "feature";
  if (/^(icms?|service issues?|icms? and service issues?)$/.test(normalizedSource)) return "icm";
  return source.trim() || "Other";
};

const getWorkItemGroupLabel = (source: string) => {
  if (source === "feature") return "Feature requests";
  if (source === "icm") return "IcMs and service issues";
  return `${source} items`;
};

const formatTimeframeLabel = (value: string) => (value === "AllUp" ? "All Up" : value);

const formatFeedbackDate = (value: string | null) => {
  if (!value) return "Not available";

  const dateOnly = value.split("T")[0];
  const [year, month, day] = dateOnly.split("-").map(Number);
  const date = year && month && day ? new Date(Date.UTC(year, month - 1, day)) : new Date(value);

  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

const parseContactDetails = (value: string | null) =>
  (value ?? "")
    .split(",")
    .map((contact) => contact.trim())
    .filter(Boolean);

const parseSummaryBullets = (value: string): SummaryBullet[] => {
  const rawLines = value.split(/\r?\n/);
  const lines = rawLines.filter((line) => line.trim());
  const sectionHeadingPattern =
    /^(?:Usage(?:\s*[-–—]\s*.+)?|Customer feedback|UX research|Competitive insights|Needs attention)\s*:?\s*$/i;
  const bulletPattern = /^(\s*)(?:[•·]|[-*])\s*(.+)$/;
  const indentationLevels = Array.from(
    new Set(
      lines
        .map((line) => line.match(bulletPattern)?.[1].replace(/\t/g, "    ").length)
        .filter((indentation): indentation is number => indentation !== undefined)
    )
  ).sort((left, right) => left - right);

  if (lines.some((line) => sectionHeadingPattern.test(line.trim()))) {
    const sections: SummaryBullet[] = [];
    let paragraphBreak = true;
    for (const line of rawLines) {
      const text = line.trim();
      if (!text) {
        paragraphBreak = true;
        continue;
      }
      const bullet = line.match(bulletPattern);
      if (sectionHeadingPattern.test(text)) {
        sections.push({ text, level: 0, kind: "heading" });
      } else if (bullet) {
        const indentation = bullet[1].replace(/\t/g, "    ").length;
        sections.push({
          text: bullet[2].trim(),
          level: indentationLevels.indexOf(indentation) + 1,
          kind: "bullet"
        });
      } else {
        const previous = sections[sections.length - 1];
        if (
          previous &&
          previous.kind !== "heading" &&
          !paragraphBreak &&
          !/^Explore the visualizations below\b/i.test(text)
        ) {
          previous.text += ` ${text}`;
        } else {
          sections.push({ text, level: 0, kind: "paragraph" });
        }
      }
      paragraphBreak = false;
    }
    return sections;
  }
  let items: SummaryBullet[];

  if (lines.length <= 1) {
    items = value
      .split(/\s*[•·]\s*/)
      .map((text) => text.trim())
      .filter(Boolean)
      .map((text) => ({ text, level: 0 }));
  } else {
    items = lines.reduce<SummaryBullet[]>((summaryItems, line) => {
      const match = line.match(bulletPattern);

      if (match) {
        const indentation = match[1].replace(/\t/g, "    ").length;
        summaryItems.push({
          text: match[2].trim(),
          level: Math.max(0, indentationLevels.indexOf(indentation))
        });
      } else if (summaryItems.length > 0) {
        summaryItems[summaryItems.length - 1].text += ` ${line.trim()}`;
      } else {
        summaryItems.push({ text: line.trim(), level: 0 });
      }

      return summaryItems;
    }, []);
  }

  return items.flatMap((item) => {
    if (item.level !== 0) return [item];

    const mixLine = item.text.match(/^(.+?[.!?])\s+((?:Customer signals mix|Feedback mix):\s*.+)$/i);
    return mixLine
      ? [
          { text: mixLine[1].trim(), level: 0 },
          { text: mixLine[2].trim(), level: 0 }
        ]
      : [item];
  });
};

const getDashboardStatusColor = (statusLabel: string): string | undefined => {
  const normalizedLabel = statusLabel.trim().toLowerCase();
  if (normalizedLabel === "open") return palette.blue;
  if (normalizedLabel === "closed") return palette.green;
  if (normalizedLabel.startsWith("not applicable")) return palette.muted;
  return undefined;
};

const getMetricStatusColor = (statusLabel: string, cardColor: string) => {
  const dashboardStatusColor = getDashboardStatusColor(statusLabel);
  if (dashboardStatusColor) return dashboardStatusColor;
  const normalizedLabel = statusLabel.toLowerCase();
  if (normalizedLabel.includes("preview")) return "#7F1FEA";
  if (normalizedLabel.includes("available")) return palette.green;
  if (normalizedLabel.includes("reject")) return palette.red;
  return cardColor;
};

const statusDistributionColors = [
  palette.purple,
  palette.cyan,
  palette.blue,
  palette.teal,
  palette.green,
  palette.red,
  palette.muted,
  palette.orange,
  palette.amber
];

const getStatusDistributionColor = (statusLabel: string, index: number) => {
  const dashboardStatusColor = getDashboardStatusColor(statusLabel);
  if (dashboardStatusColor) return dashboardStatusColor;
  const normalizedLabel = statusLabel.toLowerCase();
  if (normalizedLabel.includes("active")) return palette.orange;
  if (normalizedLabel.includes("mitigated")) return palette.amber;
  if (normalizedLabel.includes("resolved") || normalizedLabel.includes("available")) return palette.green;
  if (normalizedLabel.includes("reject")) return palette.red;
  if (normalizedLabel.includes("unspecified")) return palette.muted;
  return statusDistributionColors[index % statusDistributionColors.length];
};

const formatSourceList = (sources: string[]) => {
  if (sources.length <= 1) return sources[0] ?? "";
  if (sources.length === 2) return `${sources[0]} and ${sources[1]}`;
  return `${sources.slice(0, -1).join(", ")}, and ${sources[sources.length - 1]}`;
};

const createAskDraft = (productName: string) =>
  `Hi CVC-IQ team,\n\nI'm following up on the latest ${productName} engagement signals and recommended follow-through.\n\nCould you review the current context and confirm the next action, owner, and timing? Please also flag any missing customer evidence or status changes that should be reflected in the dashboard.\n\nThank you.`;

// const decisions = [
//   { name: "Unified investigation workflow", meta: "31 customers · 4 products · $860M represented", score: 92 },
//   { name: "Granular delegated administration", meta: "24 customers · 3 products · recurring 7 quarters", score: 88 },
//   { name: "Faster time-to-value guidance", meta: "43 customers · 6 regions · high adoption impact", score: 85 },
//   { name: "Outcome reporting APIs", meta: "18 customers · 5 partners · strategic alignment", score: 81 }
// ];

// const customerVoices = [
//   {
//     quote: "We need one investigation story across products—not five consoles and five different handoffs.",
//     source: "Strategic customer · Financial services",
//     signal: "High urgency",
//     color: palette.orange
//   },
//   {
//     quote: "The technology is strong, but deployment guidance has to reflect our operating model and constraints.",
//     source: "Enterprise customer · Public sector",
//     signal: "Adoption risk",
//     color: palette.orange
//   },
//   {
//     quote: "Help me show the board which security investments actually changed our risk posture.",
//     source: "CISO roundtable · Manufacturing",
//     signal: "Value signal",
//     color: palette.purple
//   }
// ];

const CardHeading: React.FC<{ title: string; subtitle: string; action?: React.ReactNode }> = ({
  title,
  subtitle,
  action
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
    <Box>
      <Typography sx={{ color: palette.navy, fontSize: 15, fontWeight: 750 }}>{title}</Typography>
      <Typography sx={{ mt: 0.25, color: palette.muted, fontSize: 13 }}>{subtitle}</Typography>
    </Box>
    {action}
  </Box>
);

const StatusDistribution: React.FC<{
  title: string;
  total: number;
  entries: { label: string; value: number; color: string }[];
}> = ({ title, total, entries }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ mb: 0.5, textAlign: "center", fontSize: 14, fontWeight: 750 }}>{title}</Typography>
    <Box
      sx={{
        position: "relative",
        width: 240,
        height: 200,
        mx: "auto",
        display: "grid",
        placeItems: "center",
        overflow: "hidden"
      }}
    >
      <FluentDonutChart
        data={{
          chartTitle: title,
          chartData: entries.map((entry) => ({
            legend: entry.label,
            data: entry.value,
            color: entry.color
          }))
        }}
        height={200}
        width={240}
        innerRadius={38}
        valueInsideDonut={total}
        hideLegend
        hideLabels={false}
        showLabelsInPercent
        styles={{ root: { margin: 0 } }}
      />
      {/* <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          textAlign: "center",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none"
        }}
      >
        <Typography sx={{ fontSize: 18, lineHeight: 1, fontWeight: 800 }}>{total}</Typography>
        <Typography sx={{ mt: 0.4, color: palette.muted, fontSize: 9 }}>
          {title === "Feature requests" ? "total FRs" : "total IcMs"}
        </Typography>
      </Box> */}
    </Box>
    <Stack spacing={0.75}>
      {entries.map((entry) => (
        <Box
          key={entry.label}
          sx={{ display: "grid", gridTemplateColumns: "8px minmax(0, 1fr) auto", alignItems: "center", gap: 0.7 }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: entry.color }} />
          <Typography sx={{ color: palette.muted, fontSize: 14 }}>{entry.label}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 750 }}>{entry.value}</Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

type NeedsAttentionSortKey =
  "FeedbackId" | "FeedbackType" | "FeedbackTitle" | "FeedbackDescription" | "Status" | "Severity";

const needsAttentionColumns: { label: string; sortKey: NeedsAttentionSortKey }[] = [
  { label: "Feedback ID", sortKey: "FeedbackId" },
  { label: "Type", sortKey: "FeedbackType" },
  { label: "Title", sortKey: "FeedbackTitle" },
  { label: "Description", sortKey: "FeedbackDescription" },
  { label: "Status", sortKey: "Status" },
  { label: "Severity", sortKey: "Severity" }
];

const NeedsAttentionPanel: React.FC<{
  rows: ProductInsightDataStatus[];
  themeSummary: string;
  isLoading: boolean;
  isError: boolean;
  paginationKey: string;
}> = ({ rows, themeSummary, isLoading, isError, paginationKey }) => {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<NeedsAttentionSortKey>("FeedbackId");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const rowsPerPage = 5;

  const sortedRows = useMemo(
    () =>
      rows
        .map((row, originalIndex) => ({ row, originalIndex }))
        .sort((left, right) => {
          const leftValue = left.row[sortKey];
          const rightValue = right.row[sortKey];
          let comparison = 0;

          if (typeof leftValue === "number" && typeof rightValue === "number") {
            comparison = leftValue - rightValue;
          } else {
            comparison = String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
              numeric: true,
              sensitivity: "base"
            });
          }

          if (comparison === 0) return left.originalIndex - right.originalIndex;
          return sortDirection === "asc" ? comparison : -comparison;
        })
        .map(({ row }) => row),
    [rows, sortDirection, sortKey]
  );
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const feedbackTypeSummary = useMemo(() => {
    if (rows.length === 0) return "0 items";

    const typeCounts = rows.reduce<Map<string, number>>((counts, { FeedbackType }) => {
      const feedbackType = FeedbackType?.trim() || "Feedback";
      counts.set(feedbackType, (counts.get(feedbackType) ?? 0) + 1);
      return counts;
    }, new Map());

    return Array.from(typeCounts.entries())
      .map(([feedbackType, count]) => `${count} ${feedbackType}`)
      .join(" · ");
  }, [rows]);

  const handleSort = (nextSortKey: NeedsAttentionSortKey) => {
    setSortDirection((currentDirection) => (sortKey === nextSortKey && currentDirection === "asc" ? "desc" : "asc"));
    setSortKey(nextSortKey);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [paginationKey]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [rows.length]);

  return (
    <Paper elevation={0} sx={{ ...surface, overflow: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <CardHeading
          title="Needs attention"
          subtitle={themeSummary}
          action={
            <Chip
              label={feedbackTypeSummary}
              size="small"
              sx={{ height: 27, bgcolor: "#FDE7E9", color: palette.red, fontSize: 9, fontWeight: 800 }}
            />
          }
        />
      </Box>
      <Divider />
      <TableContainer>
        <Table size="small" aria-label="Feedback needing attention">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#F3F6F9",
                "& .MuiTableCell-root, & .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active":
                  {
                    color: "rgba(0, 0, 0, 0.87) !important",
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.87) !important"
                  }
              }}
            >
              {needsAttentionColumns.map(({ label, sortKey: columnSortKey }) => (
                <TableCell key={columnSortKey} sx={{ color: palette.navy, fontSize: 11, fontWeight: 800 }}>
                  <TableSortLabel
                    active={sortKey === columnSortKey}
                    direction={sortKey === columnSortKey ? sortDirection : "asc"}
                    onClick={() => handleSort(columnSortKey)}
                    sx={{ color: "inherit !important", fontSize: "inherit", fontWeight: "inherit" }}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody key={`needs-attention-page-${page}`}>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} aria-label="Loading feedback needing attention" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: palette.red, fontSize: 10 }}>
                  Unable to load feedback needing attention.
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !isError &&
              visibleRows.map((row, index) => (
                <TableRow key={`${page * rowsPerPage + index}-${row.FeedbackId ?? "feedback"}`} hover>
                  <TableCell
                    sx={{
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      overflow: "hidden",
                      color: palette.blue,
                      fontSize: 12,
                      fontWeight: 700,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {row.FeedbackUrl?.trim() && row.FeedbackId?.trim() ? (
                      <Typography
                        component="a"
                        href={row.FeedbackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={row.FeedbackTitle ?? row.FeedbackId}
                        sx={{
                          color: "inherit",
                          fontSize: "inherit",
                          fontWeight: "inherit",
                          textDecoration: "underline"
                        }}
                      >
                        {row.FeedbackId}
                      </Typography>
                    ) : (
                      (row.FeedbackId ?? "Not available")
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <Chip
                      label={row.FeedbackType ?? "Not available"}
                      size="small"
                      sx={{ height: 22, bgcolor: "#E5F1FB", color: palette.blue, fontSize: 12, fontWeight: 800 }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 180,
                      color: palette.navy,
                      fontSize: 12,
                      whiteSpace: "normal",
                      wordBreak: "break-word"
                    }}
                  >
                    {row.FeedbackTitle?.trim() ? row.FeedbackTitle : "Not available"}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 180,
                      color: palette.navy,
                      fontSize: 12,
                      whiteSpace: "normal",
                      wordBreak: "break-word"
                    }}
                  >
                    {row.FeedbackDescription ?? "Not available"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <Chip
                      label={row.Status ?? "Not available"}
                      size="small"
                      sx={{ height: 22, bgcolor: "#DFF6EA", color: "#087A55", fontSize: 12, fontWeight: 800 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <Chip
                      label={row.Severity?.trim() ? row.Severity : "N/A"}
                      size="small"
                      sx={{
                        height: 22,
                        minWidth: 48,
                        bgcolor: "#FDE7E9",
                        color: palette.red,
                        fontSize: 12,
                        fontWeight: 800
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: palette.muted, fontSize: 12 }}>
                  No feedback currently needs attention.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isLoading && !isError && rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          labelDisplayedRows={({ from, to, count }) => `Showing ${from}-${to} of ${count} items`}
          sx={{
            color: palette.muted,
            "& .MuiTablePagination-selectLabel": { fontSize: 12 },
            "& .MuiTablePagination-displayedRows": { fontSize: 12 }
          }}
        />
      )}
    </Paper>
  );
};

const EngagementDashboard: React.FC = () => {
  const [segment, setSegment] = useState("All segments");
  const [region, setRegion] = useState("Worldwide");
  const [product, setProduct] = useState("");
  const [timeframe, setTimeframe] = useState("3 Months");
  const [themeSource, setThemeSource] = useState("");
  const [expandedNeedTheme, setExpandedNeedTheme] = useState<AssessmentTheme | null>(null);
  const [contributingFeedbackTab, setContributingFeedbackTab] = useState<"feedback" | "assessment">("feedback");
  const [search, setSearch] = useState("");
  const [customerColumnFilters, setCustomerColumnFilters] =
    useState<CustomerColumnFilters>(initialCustomerColumnFilters);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [customerSortKey, setCustomerSortKey] = useState<CustomerSortKey>("customer");
  const [customerSortDirection, setCustomerSortDirection] = useState<"asc" | "desc">("asc");
  const [contributingFeedbackSortKey, setContributingFeedbackSortKey] =
    useState<ContributingFeedbackSortKey>("feedbackId");
  const [contributingFeedbackSortDirection, setContributingFeedbackSortDirection] = useState<"asc" | "desc">("asc");
  const [contributingFeedbackColumnFilters, setContributingFeedbackColumnFilters] =
    useState<ContributingFeedbackColumnFilters>(initialContributingFeedbackColumnFilters);
  const [contributingFeedbackPage, setContributingFeedbackPage] = useState(0);
  const [contributingFeedbackRowsPerPage, setContributingFeedbackRowsPerPage] = useState(10);
  const [needThemeSearch, setNeedThemeSearch] = useState("");
  const [needSignalStatus, setNeedSignalStatus] = useState<"open" | "all">("open");
  const [needThemeSort, setNeedThemeSort] = useState<"volume-desc" | "volume-asc" | "name-asc">("volume-desc");
  const [showAllNeedThemes, setShowAllNeedThemes] = useState(false);
  const [collapsedNeedPillars, setCollapsedNeedPillars] = useState<string[]>([]);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [feedback, setFeedback] = useState<"helpful" | "not-helpful" | null>(null);
  const [feedbackExplanation, setFeedbackExplanation] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerFeedbackSnapshotResponse | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askRecipient, setAskRecipient] = useState("CVC-IQ");
  const [askSubject, setAskSubject] = useState("");
  const [askDraft, setAskDraft] = useState("");
  // const [outreachCustomer, setOutreachCustomer] = useState<CustomerFeedbackSnapshotResponse | null>(null);
  const [notification, setNotification] = useState("");
  const selectedCustomerWorkItems = useMemo(
    () => parseConsolidatedWorkItems(selectedCustomer?.ConsolidatedWorkItem ?? null),
    [selectedCustomer?.ConsolidatedWorkItem]
  );
  const selectedCustomerContacts = useMemo(
    () => parseContactDetails(selectedCustomer?.EngagementOwner ?? null),
    [selectedCustomer?.EngagementOwner]
  );
  const selectedCustomerWorkItemGroups = useMemo(
    () =>
      Array.from(
        selectedCustomerWorkItems.items.reduce<Map<string, ConsolidatedWorkItem[]>>((groups, item) => {
          const source = getWorkItemGroupKey(item.Source);
          const sourceItems = groups.get(source) ?? [];
          sourceItems.push(item);
          groups.set(source, sourceItems);
          return groups;
        }, new Map())
      ),
    [selectedCustomerWorkItems.items]
  );
  const executiveSummaryQuery = useGetUnifiedInsightsProductEngagementDataQuery("ExecutiveSummary");
  const competitiveAssessmentQuery = useGetUnifiedInsightsProductEngagementDataQuery("CompetitiveAssessment", {
    skip: !product
  });
  const competitiveAssessment = useMemo(
    () =>
      selectCompetitiveAssessment(competitiveAssessmentQuery.data ?? [], product, undefined, "CompetitiveAssessment"),
    [competitiveAssessmentQuery.data, product]
  );
  const themeCompetitiveAssessmentQuery = useGetUnifiedInsightsProductEngagementDataQuery(
    "TopCustomerNeedsCompetitiveAssessment",
    { skip: !product || !expandedNeedTheme }
  );
  const themeCompetitiveAssessment = useMemo(
    () =>
      expandedNeedTheme
        ? selectCompetitiveAssessment(
            themeCompetitiveAssessmentQuery.data ?? [],
            product,
            expandedNeedTheme,
            "TopCustomerNeedsCompetitiveAssessment"
          )
        : { status: "empty" as const },
    [themeCompetitiveAssessmentQuery.data, product, expandedNeedTheme]
  );
  const cardMetricsQuery = useGetUnifiedInsightsProductEngagementDataQuery("CardMetrics");
  const usageMetricsQuery = useGetUnifiedInsightsUsageMetricsQuery(undefined);
  const topCustomerNeedsQuery = useGetUnifiedInsightsProductEngagementDataQuery("TopCustomerNeeds");
  const uxResearchInsightsQuery = useGetUnifiedInsightsProductEngagementDataQuery("UXResearchInsights", {
    skip: !product
  });
  const featureICMStatusQuery = useGetUnifiedInsightsProductEngagementDataQuery("FeatureICMStatus");
  const needsAttentionQuery = useGetUnifiedInsightsProductEngagementDataQuery("NeedsAttention");
  const customerEngagementDataQuery = useGetCustomerEngagementDataQuery(
    {
      productName: product,
      feedbackPeriod: timeframe
    },
    { skip: !product || !timeframe }
  );
  const isCustomerTablePurviewProduct = (customerEngagementDataQuery.data ?? []).some(
    ({ IsPurviewProduct }) => IsPurviewProduct === true
  );
  const customerTableColumns = [
    ...customerTableBaseColumns,
    ...(isCustomerTablePurviewProduct ? customerTablePurviewColumns : customerTableFeatureColumns),
    customerTableSummaryColumn
  ];
  const needsAttentionThemeSummary = useMemo(
    () =>
      (needsAttentionQuery.data ?? []).find(
        ({ productName, feedbackPeriod, sourceLabel, themeSummary }) =>
          productName === product &&
          feedbackPeriod === timeframe &&
          sourceLabel === "All sources" &&
          Boolean(themeSummary)
      )?.themeSummary ?? "",
    [needsAttentionQuery.data, product, timeframe]
  );
  const needsAttentionRows = useMemo(
    () =>
      (needsAttentionQuery.data ?? [])
        .filter(
          ({ productName, feedbackPeriod, sourceLabel }) =>
            productName === product && feedbackPeriod === timeframe && sourceLabel === "All sources"
        )
        .flatMap(({ dataStatus }) => (dataStatus ? JSON.parse(dataStatus as string) : [])),
    [needsAttentionQuery.data, product, timeframe]
  );
  const timeframeOptions = useMemo(() => {
    const rows = [
      ...(executiveSummaryQuery.data ?? []),
      ...(cardMetricsQuery.data ?? []),
      ...(topCustomerNeedsQuery.data ?? []),
      ...(featureICMStatusQuery.data ?? [])
    ].filter(({ productName }) => productName === product);

    return timeframeOrder.map((value) => {
      const row = rows.find(({ feedbackPeriod }) => feedbackPeriod?.toLowerCase() === value.toLowerCase());
      return {
        value,
        label: formatTimeframeLabel(value),
        startDate: formatFeedbackDate(row?.feedback_StartDate ?? null),
        endDate: formatFeedbackDate(row?.feedback_EndDate ?? null)
      };
    });
  }, [
    cardMetricsQuery.data,
    executiveSummaryQuery.data,
    featureICMStatusQuery.data,
    product,
    topCustomerNeedsQuery.data
  ]);
  // const selectedTimeframeOption = timeframeOptions.find(({ value }) => value === timeframe) ?? timeframeOptions[0];
  const executiveSummaryItems = useMemo(
    () =>
      (executiveSummaryQuery.data ?? [])
        .filter(({ productName, feedbackPeriod }) => productName === product && feedbackPeriod === timeframe)
        .flatMap(({ themeSummary }) => parseSummaryBullets(themeSummary ?? "")),
    [executiveSummaryQuery.data, product, timeframe]
  );
  const visibleMetricCards = useMemo(() => {
    const groupedMetrics = (cardMetricsQuery.data ?? [])
      .filter(
        ({ productName, feedbackPeriod, isCardVisible }) =>
          productName === product && feedbackPeriod === timeframe && isCardVisible?.trim().toLowerCase() === "yes"
      )
      .reduce<Map<string, NonNullable<typeof cardMetricsQuery.data>>>((groups, metric) => {
        const metrics = groups.get(metric.metricLabel) ?? [];
        metrics.push(metric);
        groups.set(metric.metricLabel, metrics);
        return groups;
      }, new Map());

    return Array.from(groupedMetrics.entries())
      .map(([metricLabel, matchedMetrics]) => {
        const presentation = metricCards.find(({ label }) => label === metricLabel);
        const metricValue =
          matchedMetrics.find(({ metricValue: value }) => value !== null)?.metricValue ??
          matchedMetrics.reduce((total, metric) => total + (metric.statusCount ?? 0), 0);

        return {
          metricLabel,
          metricValue,
          matchedMetrics,
          color: presentation?.color ?? palette.blue,
          icon: presentation?.icon ?? AssessmentOutlined,
          sortOrder: Math.min(...matchedMetrics.map(({ sortOrder }) => sortOrder ?? Number.MAX_SAFE_INTEGER))
        };
      })
      .filter(({ metricValue }) => metricValue > 0)
      .sort((left, right) => left.sortOrder - right.sortOrder);
  }, [cardMetricsQuery.data, product, timeframe]);

  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const normalizedFilters = Object.fromEntries(
      Object.entries(customerColumnFilters).map(([key, value]) => [key, value.trim().toLowerCase()])
    ) as CustomerColumnFilters;
    const includesFilter = (value: string | number | null, filter: string) =>
      !filter ||
      String(value ?? "")
        .toLowerCase()
        .includes(filter);

    return (customerEngagementDataQuery.data ?? []).filter((customer) => {
      const matchesSegment = segment === "All segments" || customer.SegmentName === segment;
      const matchesRegion = region === "Worldwide" || customer.RegionName === region;
      const matchesSearch =
        !query ||
        Object.values(customer)
          .filter((value) => typeof value === "string" || typeof value === "number")
          .some((value) => String(value).toLowerCase().includes(query));
      const matchesFeedbackColumns = customer.IsPurviewProduct
        ? includesFilter(customer.DCRCount, normalizedFilters.dcrs) &&
          includesFilter(customer.DCRStatuses, normalizedFilters.dcrStatuses) &&
          includesFilter(customer.ADOBugCount, normalizedFilters.adoBugs) &&
          includesFilter(customer.ADOBugStatuses, normalizedFilters.adoBugStatuses)
        : includesFilter(customer.FeatureRequestCount, normalizedFilters.featureRequests) &&
          includesFilter(customer.FeatureStatuses, normalizedFilters.featureStatuses) &&
          includesFilter(customer.ICMCount, normalizedFilters.icms) &&
          includesFilter(customer.ICMStatuses, normalizedFilters.icmStatuses);
      const matchesColumns =
        includesFilter(`${customer.TenantName} ${customer.TenantId}`, normalizedFilters.customer) &&
        includesFilter(customer.ProductName, normalizedFilters.product) &&
        includesFilter(customer.CountryName ?? customer.CountryCode, normalizedFilters.country) &&
        includesFilter(customer.RegionName, normalizedFilters.region) &&
        includesFilter(customer.SegmentName, normalizedFilters.segment) &&
        matchesFeedbackColumns &&
        includesFilter(customer.EngagementSummary, normalizedFilters.engagementSummary);
      return matchesSegment && matchesRegion && matchesSearch && matchesColumns;
    });
  }, [customerColumnFilters, customerEngagementDataQuery.data, region, search, segment]);
  const sortedCustomers = useMemo(
    () =>
      filteredCustomers
        .map((customer, originalIndex) => ({ customer, originalIndex }))
        .sort((left, right) => {
          const values: Record<CustomerSortKey, [string | number | null, string | number | null]> = {
            customer: [left.customer.TenantName, right.customer.TenantName],
            product: [left.customer.ProductName, right.customer.ProductName],
            country: [
              left.customer.CountryName ?? left.customer.CountryCode,
              right.customer.CountryName ?? right.customer.CountryCode
            ],
            region: [left.customer.RegionName, right.customer.RegionName],
            segment: [left.customer.SegmentName, right.customer.SegmentName],
            featureRequests: [left.customer.FeatureRequestCount, right.customer.FeatureRequestCount],
            featureStatuses: [left.customer.FeatureStatuses, right.customer.FeatureStatuses],
            icms: [left.customer.ICMCount, right.customer.ICMCount],
            icmStatuses: [left.customer.ICMStatuses, right.customer.ICMStatuses],
            dcrs: [left.customer.DCRCount, right.customer.DCRCount],
            dcrStatuses: [left.customer.DCRStatuses, right.customer.DCRStatuses],
            adoBugs: [left.customer.ADOBugCount, right.customer.ADOBugCount],
            adoBugStatuses: [left.customer.ADOBugStatuses, right.customer.ADOBugStatuses],
            engagementSummary: [left.customer.EngagementSummary, right.customer.EngagementSummary]
          };
          const [leftValue, rightValue] = values[customerSortKey];
          const comparison =
            typeof leftValue === "number" && typeof rightValue === "number"
              ? leftValue - rightValue
              : String(leftValue).localeCompare(String(rightValue), undefined, {
                  numeric: true,
                  sensitivity: "base"
                });

          if (comparison === 0) return left.originalIndex - right.originalIndex;
          return customerSortDirection === "asc" ? comparison : -comparison;
        })
        .map(({ customer }) => customer),
    [customerSortDirection, customerSortKey, filteredCustomers]
  );
  const paginatedCustomers = sortedCustomers.slice(
    customerPage * customerRowsPerPage,
    customerPage * customerRowsPerPage + customerRowsPerPage
  );
  const handleCustomerSort = (nextSortKey: CustomerSortKey) => {
    setCustomerSortDirection((currentDirection) =>
      customerSortKey === nextSortKey && currentDirection === "asc" ? "desc" : "asc"
    );
    setCustomerSortKey(nextSortKey);
    setCustomerPage(0);
  };
  useEffect(() => {
    setCustomerPage(0);
  }, [customerColumnFilters, product, region, search, segment, timeframe]);
  useEffect(() => {
    setCustomerColumnFilters(initialCustomerColumnFilters);
    setCustomerSortKey("customer");
    setCustomerSortDirection("asc");
  }, [product]);

  const topCustomerNeedRows = useMemo(
    () =>
      (topCustomerNeedsQuery.data ?? []).filter(
        ({ productName, feedbackPeriod }) => productName === product && feedbackPeriod === timeframe
      ),
    [product, timeframe, topCustomerNeedsQuery.data]
  );
  const themeSources = useMemo(() => {
    const uniqueSources = topCustomerNeedRows
      .map(({ sourceLabel }) => sourceLabel)
      .filter((sourceLabel): sourceLabel is string => !!sourceLabel)
      .filter((sourceLabel, index, sourceLabels) => sourceLabels.indexOf(sourceLabel) === index);

    return [
      ...uniqueSources.filter((sourceLabel) => sourceLabel === "All sources"),
      ...uniqueSources.filter((sourceLabel) => sourceLabel !== "All sources")
    ];
  }, [topCustomerNeedRows]);
  const selectedThemeSource = themeSources.includes(themeSource) ? themeSource : "All sources";
  const showFeedbackType = selectedThemeSource === "All sources";
  const visibleContributingFeedbackColumns = contributingFeedbackColumns.filter(
    ({ sortKey }) => sortKey !== "feedbackTypeLabel" || showFeedbackType
  );
  const activeContributingFeedbackSortKey =
    contributingFeedbackSortKey === "feedbackTypeLabel" && !showFeedbackType
      ? "feedbackId"
      : contributingFeedbackSortKey;
  const topCustomerNeedsDrilldownQuery = useGetUnifiedInsightsTopCustomerNeedsDrilldownDataQuery(
    {
      productName: product,
      feedbackPeriod: timeframe,
      sourceLabel: selectedThemeSource
    },
    {
      skip: !product || !timeframe || !selectedThemeSource
    }
  );
  const themeFeedbackRows = useMemo(
    () =>
      (topCustomerNeedsDrilldownQuery.data ?? []).filter(
        ({ productName, feedbackPeriod, sourceLabel, themeName, feedbackDashboardStatus }) =>
          productName === product &&
          feedbackPeriod === timeframe &&
          (selectedThemeSource === "All sources" || sourceLabel === selectedThemeSource) &&
          themeName === expandedNeedTheme?.name &&
          (needSignalStatus === "all" || feedbackDashboardStatus?.trim().toLowerCase() === "open")
      ),
    [topCustomerNeedsDrilldownQuery.data, product, timeframe, selectedThemeSource, expandedNeedTheme, needSignalStatus]
  );
  const topCustomerNeedDrilldownRows = useMemo(() => {
    const normalizedFilters = Object.fromEntries(
      Object.entries(contributingFeedbackColumnFilters).map(([key, value]) => [key, value.trim().toLowerCase()])
    ) as ContributingFeedbackColumnFilters;
    const includesFilter = (value: unknown, filter: string) =>
      !filter ||
      String(value ?? "")
        .toLowerCase()
        .includes(filter);

    return themeFeedbackRows
      .filter(
        (feedback) =>
          includesFilter(feedback.feedbackId, normalizedFilters.feedbackId) &&
          (!showFeedbackType || includesFilter(feedback.feedbackTypeLabel, normalizedFilters.feedbackTypeLabel)) &&
          includesFilter(feedback.feedbackTitle, normalizedFilters.feedbackTitle) &&
          includesFilter(feedback.customerCount, normalizedFilters.customerCount) &&
          includesFilter(feedback.customers, normalizedFilters.customers) &&
          includesFilter(feedback.feedbackDetails, normalizedFilters.feedbackDetails) &&
          includesFilter(feedback.feedbackStatus, normalizedFilters.feedbackStatus)
      )
      .map((row, originalIndex) => ({ row, originalIndex }))
      .sort((left, right) => {
        const comparison = String(left.row[activeContributingFeedbackSortKey] ?? "").localeCompare(
          String(right.row[activeContributingFeedbackSortKey] ?? ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base"
          }
        );

        if (comparison === 0) return left.originalIndex - right.originalIndex;
        return contributingFeedbackSortDirection === "asc" ? comparison : -comparison;
      })
      .map(({ row }) => row);
  }, [
    themeFeedbackRows,
    contributingFeedbackColumnFilters,
    contributingFeedbackSortDirection,
    activeContributingFeedbackSortKey,
    showFeedbackType
  ]);
  const paginatedTopCustomerNeedDrilldownRows = topCustomerNeedDrilldownRows.slice(
    contributingFeedbackPage * contributingFeedbackRowsPerPage,
    contributingFeedbackPage * contributingFeedbackRowsPerPage + contributingFeedbackRowsPerPage
  );
  const handleContributingFeedbackSort = (nextSortKey: ContributingFeedbackSortKey) => {
    setContributingFeedbackSortDirection((currentDirection) =>
      activeContributingFeedbackSortKey === nextSortKey && currentDirection === "asc" ? "desc" : "asc"
    );
    setContributingFeedbackSortKey(nextSortKey);
    setContributingFeedbackPage(0);
  };
  useEffect(() => {
    setContributingFeedbackPage(0);
  }, [contributingFeedbackColumnFilters, expandedNeedTheme, product, selectedThemeSource, timeframe, needSignalStatus]);
  const sourceNeedThemes = useMemo(() => {
    const metrics = new Map<
      string,
      { name: string; pillar: string; value: number; open: number; closed: number; hasStatusData: boolean }
    >();

    topCustomerNeedRows
      .filter(({ sourceLabel }) => sourceLabel === selectedThemeSource)
      .forEach(({ metricLabel, metricValue, themePillar, dashboardStatusDistribution }) => {
        const pillar = themePillar?.trim() || "Other";
        const key = `${pillar}\u0000${metricLabel}`;
        const metric = metrics.get(key);
        let distribution: unknown = dashboardStatusDistribution;
        if (typeof distribution === "string") {
          try {
            distribution = JSON.parse(distribution);
          } catch (error) {
            if (!(error instanceof SyntaxError)) throw error;
            distribution = null;
          }
        }
        let open = 0;
        let closed = 0;
        let hasStatusData = Array.isArray(distribution) && distribution.length > 0;
        if (Array.isArray(distribution)) {
          distribution.forEach((entry: unknown) => {
            if (typeof entry !== "object" || entry === null) {
              hasStatusData = false;
              return;
            }
            const status =
              ("dashboardStatus" in entry ? entry.dashboardStatus : null) ??
              ("DashboardStatus" in entry ? entry.DashboardStatus : null);
            const count =
              ("count" in entry ? entry.count : null) ?? ("Count" in entry ? entry.Count : null);
            if (
              typeof status !== "string" ||
              !["open", "closed"].includes(status.trim().toLowerCase()) ||
              typeof count !== "number" ||
              !Number.isFinite(count) ||
              count < 0
            ) {
              hasStatusData = false;
              return;
            }
            if (status.trim().toLowerCase() === "open") open += count;
            else closed += count;
          });
        }
        metrics.set(key, {
          name: metricLabel,
          pillar,
          value: (metric?.value ?? 0) + (hasStatusData ? open + closed : (metricValue ?? 0)),
          open: (metric?.open ?? 0) + open,
          closed: (metric?.closed ?? 0) + closed,
          hasStatusData: (metric?.hasStatusData ?? true) && hasStatusData
        });
      });

    return Array.from(metrics.values());
  }, [selectedThemeSource, topCustomerNeedRows]);
  const openNeedSignalCount = sourceNeedThemes.reduce(
    (total, theme) => total + (theme.hasStatusData ? theme.open : 0),
    0
  );
  const allNeedThemes = useMemo(
    () => sourceNeedThemes
      .filter((theme) => needSignalStatus === "all" || (theme.hasStatusData && theme.open > 0))
      .map((theme) =>
        needSignalStatus === "open" ? { ...theme, value: theme.open, closed: 0 } : theme
      )
      .sort((left, right) => right.value - left.value),
    [sourceNeedThemes, needSignalStatus]
  );
  const displayedNeedThemes = useMemo(() => {
    const normalizedSearch = needThemeSearch.trim().toLowerCase();
    const filteredThemes = allNeedThemes.filter(({ name }) => name.toLowerCase().includes(normalizedSearch));
    const sortedThemes = [...filteredThemes].sort((left, right) => {
      if (needThemeSort === "name-asc") return left.name.localeCompare(right.name);
      return needThemeSort === "volume-asc" ? left.value - right.value : right.value - left.value;
    });
    return showAllNeedThemes ? sortedThemes : sortedThemes.slice(0, 8);
  }, [allNeedThemes, needThemeSearch, needThemeSort, showAllNeedThemes]);
  const displayedNeedThemeGroups = useMemo(() => {
    const groups = new Map<string, typeof displayedNeedThemes>();
    displayedNeedThemes.forEach((theme) => {
      const themes = groups.get(theme.pillar) ?? [];
      themes.push(theme);
      groups.set(theme.pillar, themes);
    });

    return Array.from(groups, ([pillar, themes]) => ({ pillar, themes })).sort(
      (left, right) =>
        (needPillarStyles[left.pillar]?.order ?? needPillarStyles.Other.order) -
        (needPillarStyles[right.pillar]?.order ?? needPillarStyles.Other.order)
    );
  }, [displayedNeedThemes]);
  const matchingNeedThemeCount = allNeedThemes.filter(({ name }) =>
    name.toLowerCase().includes(needThemeSearch.trim().toLowerCase())
  ).length;
  const maximumThemeValue = Math.max(1, ...allNeedThemes.map((theme) => theme.value));
  useEffect(() => {
    if (
      expandedNeedTheme &&
      !allNeedThemes.some(({ name, pillar }) => name === expandedNeedTheme.name && pillar === expandedNeedTheme.pillar)
    ) {
      setExpandedNeedTheme(null);
    }
  }, [allNeedThemes, expandedNeedTheme]);
  useEffect(() => {
    setNeedThemeSearch("");
    setShowAllNeedThemes(false);
    setCollapsedNeedPillars([]);
  }, [product, selectedThemeSource, timeframe]);
  const statusDistributions = useMemo(() => {
    const availableSources = new Map(
      themeSources
        .filter((sourceLabel) => sourceLabel.toLowerCase() !== "all sources")
        .map((sourceLabel, index) => [sourceLabel.toLowerCase(), index])
    );
    const sources = new Map<
      string,
      {
        label: string;
        order: number;
        statuses: Map<string, { label: string; value: number; order: number }>;
      }
    >();
    const addStatus = (
      sourceLabel: string,
      statusLabel: string,
      value: number,
      sourceOrder: number,
      statusOrder: number
    ) => {
      const sourceKey = sourceLabel.toLowerCase();
      const source = sources.get(sourceKey) ?? {
        label: sourceLabel,
        order: sourceOrder,
        statuses: new Map<string, { label: string; value: number; order: number }>()
      };
      const statusKey = statusLabel.toLowerCase();
      const status = source.statuses.get(statusKey);
      source.statuses.set(statusKey, {
        label: status?.label ?? statusLabel,
        value: (status?.value ?? 0) + value,
        order: Math.min(status?.order ?? Number.MAX_SAFE_INTEGER, statusOrder)
      });
      sources.set(sourceKey, source);
    };

    (featureICMStatusQuery.data ?? [])
      .filter(({ productName, feedbackPeriod }) => productName === product && feedbackPeriod === timeframe)
      .forEach((row, rowIndex) => {
        const sourceLabel = row.sourceLabel?.trim() || row.sourceType?.trim();
        const statusLabel = row.statusLabel?.trim() || row.metricLabel?.trim();
        const sourceOrder = sourceLabel ? availableSources.get(sourceLabel.toLowerCase()) : undefined;
        if (!sourceLabel || sourceOrder === undefined || !statusLabel) return;

        addStatus(
          sourceLabel,
          statusLabel,
          row.statusCount ?? row.metricValue ?? 0,
          sourceOrder,
          row.statusOrder ?? row.sortOrder ?? rowIndex
        );
      });

    const dedicatedStatusSources = new Set(sources.keys());
    const cardMetricSources = (cardMetricsQuery.data ?? [])
      .filter(({ productName, feedbackPeriod }) => productName === product && feedbackPeriod === timeframe)
      .reduce<Map<string, NonNullable<typeof cardMetricsQuery.data>>>((groupedSources, row) => {
        const sourceLabel = row.metricLabel?.trim();
        if (
          !sourceLabel ||
          !availableSources.has(sourceLabel.toLowerCase()) ||
          dedicatedStatusSources.has(sourceLabel.toLowerCase())
        ) {
          return groupedSources;
        }

        const sourceRows = groupedSources.get(sourceLabel) ?? [];
        sourceRows.push(row);
        groupedSources.set(sourceLabel, sourceRows);
        return groupedSources;
      }, new Map());

    Array.from(cardMetricSources.entries()).forEach(([sourceLabel, rows], sourceIndex) => {
      const statusRows = rows.filter(({ statusLabel }) => statusLabel?.trim());
      const sourceOrder = availableSources.get(sourceLabel.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;

      if (statusRows.length > 0) {
        statusRows.forEach((row, statusIndex) => {
          const statusLabel = row.statusLabel?.trim();
          if (!statusLabel) return;

          addStatus(sourceLabel, statusLabel, row.statusCount ?? 0, sourceOrder, row.statusOrder ?? statusIndex);
        });
        return;
      }

      const total =
        rows.find(({ metricValue }) => metricValue !== null)?.metricValue ??
        rows.reduce((sum, { statusCount }) => sum + (statusCount ?? 0), 0);
      addStatus(sourceLabel, "Total responses", total, sourceOrder, sourceIndex);
    });

    return Array.from(sources.values())
      .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
      .map((source) => {
        const entries = Array.from(source.statuses.values())
          .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
          .map((status, index) => ({
            label: status.label,
            value: status.value,
            color: getStatusDistributionColor(status.label, index)
          }));

        return {
          title: source.label,
          total: entries.reduce((total, entry) => total + entry.value, 0),
          entries
        };
      });
  }, [cardMetricsQuery.data, featureICMStatusQuery.data, product, themeSources, timeframe]);
  const statusDistributionTitle =
    statusDistributions.length > 0
      ? `${formatSourceList(statusDistributions.map(({ title }) => title))} status`
      : "Status distribution";

  // const openAskDialog = () => {
  //   setAskRecipient("CVC-IQ");
  //   setAskSubject(`CVC-IQ engagement follow-up — ${product}`);
  //   setAskDraft(createAskDraft(product));
  //   setAskOpen(true);
  // };

  const resetFilters = () => {
    setTimeframe("3 Months");
    setSegment("All segments");
    setRegion("Worldwide");
    setProduct("");
    setThemeSource("");
    setSearch("");
    setCustomerColumnFilters(initialCustomerColumnFilters);
    setCustomerPage(0);
  };

  if (executiveSummaryQuery.isLoading)
    return (
      <Box sx={{ minHeight: 400, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ minHeight: "100%", bgcolor: palette.canvas, color: palette.ink }}>
      <Paper
        component="section"
        square
        elevation={0}
        aria-label="Dashboard filters"
        sx={{ p: 2, borderBottom: `1px solid ${palette.line}` }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
          {/* <TextField
            label="Start date"
            type="date"
            size="small"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel>Customer segment</InputLabel>
            <Select value={segment} label="Customer segment" onChange={(event) => setSegment(event.target.value)}>
              {["All segments", "Strategic", "Enterprise", "SMC", "Public sector"].map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Region</InputLabel>
            <Select value={region} label="Region" onChange={(event) => setRegion(event.target.value)}>
              {["Worldwide", "Americas", "EMEA", "Asia Pacific"].map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          <FormControl
            size="small"
            sx={{ minWidth: 210, p: 0.75, pt: 2, bgcolor: "#EEF6FC", border: "1px solid #9FC4E2", borderRadius: 1 }}
          >
            <InputLabel shrink sx={{ ml: 0.75, mt: 0.75, top: 10 }}>
              Product view
            </InputLabel>
            <Select
              value={product}
              label="Product view"
              onChange={(event) => setProduct(event.target.value)}
              displayEmpty
              renderValue={(value) => value || "Select a product"}
              sx={{ bgcolor: "#FFF", fontWeight: 700 }}
            >
              <MenuItem value="" disabled>
                Select a product
              </MenuItem>
              {(executiveSummaryQuery.data ?? [])
                .map((item) => item.productName)
                .filter((name): name is string => !!name)
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" }))
                .map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{ minWidth: 150, p: 0.75, pt: 2, bgcolor: "#EEF6FC", border: "1px solid #9FC4E2", borderRadius: 1 }}
          >
            <InputLabel sx={{ ml: 0.75, mt: 0.75, top: 10 }}>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              disabled={!product}
              onChange={(event) => setTimeframe(event.target.value)}
              sx={{ bgcolor: "#FFF" }}
              renderValue={(value) => formatTimeframeLabel(value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    width: 380,
                    maxWidth: "calc(100vw - 32px)",
                    borderRadius: 1.5,
                    boxShadow: "0 8px 24px rgba(15, 45, 75, 0.18)"
                  }
                },
                MenuListProps: { sx: { p: 0 } }
              }}
            >
              {timeframeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    minHeight: 76,
                    px: 2,
                    py: 1.3,
                    gap: 1.5,
                    borderBottom: `1px solid ${palette.line}`,
                    whiteSpace: "normal",
                    "&.Mui-selected": {
                      bgcolor: "#EEF6FF",
                      "&:hover": { bgcolor: "#E7F1FC" }
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 1,
                      bgcolor: "#EDF4FD",
                      color: "#0F6CBD"
                    }}
                  >
                    <CalendarMonthOutlined />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "inherit", color: palette.ink }}>{option.label}</Typography>
                    <Typography sx={{ mt: 0.35, color: palette.muted, fontSize: 11 }}>
                      Start: {option.startDate}&nbsp;&nbsp;•&nbsp;&nbsp;End: {option.endDate}
                    </Typography>
                  </Box>
                  {timeframe === option.value && <Check sx={{ color: "#0F6CBD" }} />}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              ml: { xl: "auto", lg: "auto" },
              display: "flex",
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <Button variant="outlined" onClick={resetFilters} sx={{ textTransform: "none" }}>
              Reset
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box component="main" sx={{ maxWidth: 1600, mx: "auto", p: { xs: 2, md: 3 }, px: "0px !important" }}>
        {!product ? (
          <Paper
            elevation={0}
            sx={{
              ...surface,
              minHeight: 360,
              display: "grid",
              placeItems: "center",
              p: { xs: 3, md: 5 },
              textAlign: "center",
              bgcolor: "#FBFDFF"
            }}
          >
            <Box sx={{ maxWidth: 520 }}>
              <Box
                aria-hidden="true"
                sx={{
                  mx: "auto",
                  width: 64,
                  height: 64,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: "#E8F3FC",
                  color: palette.blue
                }}
              >
                <AssessmentOutlined sx={{ fontSize: 32 }} />
              </Box>
              <Typography component="h1" sx={{ mt: 2, color: palette.navy, fontSize: 20, fontWeight: 800 }}>
                Select a product to view engagement insights
              </Typography>
              <Typography sx={{ mt: 0.8, color: palette.muted, fontSize: 12, lineHeight: 1.6 }}>
                Choose a product from the Product view filter above to load its executive summary, customer needs,
                status metrics, and feedback requiring attention.
              </Typography>
              <Chip
                label="Use the Product view filter to get started"
                size="small"
                sx={{ mt: 2, height: 26, bgcolor: "#EEF6FC", color: palette.blue, fontSize: 10, fontWeight: 700 }}
              />
            </Box>
          </Paper>
        ) : (
          <>
            <Box>
              <Typography component="h1" sx={{ color: palette.navy, fontSize: { xs: 24, md: 30 }, fontWeight: 750 }}>
                {product} Engagement Dashboard
              </Typography>
              <Typography sx={{ mt: 0.5, color: palette.muted, fontSize: 13 }}>
                Executive view of customer demand, evidence strength, accountable action, and closed-loop outcomes.
              </Typography>
            </Box>

            <UsageMetricsCards
              data={usageMetricsQuery.data ?? []}
              product={product}
              timeframe={timeframe}
              isFetching={usageMetricsQuery.isFetching}
              isError={usageMetricsQuery.isError}
              onRetry={() => usageMetricsQuery.refetch()}
            >
              {visibleMetricCards.map(({ metricLabel, metricValue, matchedMetrics, color, icon: MetricIcon }) => {
                const statusEntries = Array.from(
                  matchedMetrics.reduce<Map<string, { count: number; order: number }>>((statuses, metric) => {
                    const statusLabel =
                      metric.statusLabel?.trim() ||
                      (metricLabel.trim().toLowerCase() === "survey responses" ? "Not applicable" : "");
                    if (!statusLabel) return statuses;
                    const existing = statuses.get(statusLabel);
                    statuses.set(statusLabel, {
                      count: (existing?.count ?? 0) + (metric.statusCount ?? 0),
                      order: Math.min(
                        existing?.order ?? Number.MAX_SAFE_INTEGER,
                        metric.statusOrder ?? Number.MAX_SAFE_INTEGER
                      )
                    });
                    return statuses;
                  }, new Map())
                )
                  .map(([label, { count, order }]) => ({ label, count, order }))
                  .sort((left, right) => left.order - right.order);
                return (
                  <Paper
                    key={metricLabel}
                    elevation={0}
                    sx={{
                      ...surface,
                      position: "relative",
                      minWidth: 0,
                      overflow: "hidden",
                      minHeight: 120,
                      p: 1.5,
                      pl: 2,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "10px",
                      boxShadow: "0 2px 5px rgba(15, 45, 75, .12), 0 10px 24px rgba(15, 45, 75, .08)",
                      "&::after": {
                        content: "\"\"",
                        position: "absolute",
                        width: 92,
                        height: 92,
                        top: -50,
                        right: -38,
                        borderRadius: "50%",
                        bgcolor: `${color}10`
                      },
                      "&::before": {
                        content: "\"\"",
                        position: "absolute",
                        inset: "0 auto 0 0",
                        width: 5,
                        bgcolor: color
                      }
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          borderRadius: "8px",
                          color: "#FFF",
                          bgcolor: color,
                          boxShadow: `0 5px 12px ${color}35`
                        }}
                      >
                        <MetricIcon sx={{ fontSize: 25 }} />
                      </Box>
                      <Typography
                        sx={{ color: palette.ink, fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}
                      >
                        {metricLabel}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ mt: 0.7, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
                    >
                      <Typography sx={{ color: palette.navy, fontSize: 27, lineHeight: 1.1, fontWeight: 800 }}>
                        {metricValue}
                      </Typography>
                      {/* <Box
                      sx={{
                        px: 1.1,
                        py: 0.65,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.65,
                        borderRadius: 5,
                        color: palette.navy,
                        bgcolor: `${color}14`
                      }}
                    >
                      {timeframe === "AllUp" ? (
                        <AllInclusive sx={{ color, fontSize: 18 }} />
                      ) : (
                        <CalendarMonthOutlined sx={{ color, fontSize: 16 }} />
                      )}
                      <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                        {formatTimeframeLabel(timeframe)}
                      </Typography>
                      <KeyboardArrowDown sx={{ color, fontSize: 17 }} />
                    </Box> */}
                    </Box>
                    <Divider sx={{ my: 1.2 }} />
                    <Box sx={{ mt: "auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.8 }}>
                      {statusEntries.map(({ label }) => {
                        const statusColor = getMetricStatusColor(label, color);
                        return (
                          <Box
                            key={label}
                            sx={{
                              px: 1,
                              py: 0.55,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.7,
                              minWidth: 0,
                              maxWidth: "100%",
                              borderRadius: 5,
                              color: palette.navy,
                              bgcolor: `${statusColor}16`
                            }}
                          >
                            <Box
                              sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: "50%", bgcolor: statusColor }}
                            />
                            <Typography
                              title={label}
                              sx={{
                                minWidth: 0,
                                display: "-webkit-box",
                                overflow: "hidden",
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.3,
                                overflowWrap: "anywhere",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 3
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                      {statusEntries.length === 0 && (
                        <Typography sx={{ color: palette.muted, fontSize: 10 }}>No status data</Typography>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </UsageMetricsCards>

            <Paper
              elevation={0}
              sx={{
                ...surface,
                mt: 2,
                overflow: "hidden",
                bgcolor: "#FFFFFF",
                borderLeft: `4px solid ${palette.blue}`
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "grid",
                  gridTemplateColumns: "32px minmax(0, 1fr) auto",
                  columnGap: 1.25,
                  alignItems: "center",
                  bgcolor: "#F6FAFE",
                  background: "linear-gradient(90deg, #F7FBFF 0%, #EEF6FD 100%)"
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    width: 30,
                    height: 30,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    bgcolor: "#DDEFFF",
                    color: palette.blue
                  }}
                >
                  <AutoAwesomeOutlined sx={{ fontSize: 17 }} />
                </Box>
                <Typography component="h2" sx={{ ...insightCardTitleStyles, gridColumn: 2, gridRow: 1 }}>
                  AI-generated executive summary
                </Typography>
                <Box
                  sx={{
                    gridColumn: { xs: "1 / -1", md: 3 },
                    gridRow: { xs: 2, md: 1 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 0.75
                  }}
                >
                  {summaryExpanded && (
                    <>
                      <Typography sx={{ color: palette.muted, fontSize: 11 }}>Was this summary helpful?</Typography>
                      <Button
                        size="small"
                        variant={feedback === "helpful" ? "contained" : "outlined"}
                        startIcon={<ThumbUpOutlined />}
                        onClick={() => {
                          setFeedback("helpful");
                          setNotification("Thanks — feedback recorded.");
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Helpful
                      </Button>
                      <Button
                        size="small"
                        variant={feedback === "not-helpful" ? "contained" : "outlined"}
                        startIcon={<ThumbDownOutlined />}
                        onClick={() => setFeedback("not-helpful")}
                        sx={{ textTransform: "none" }}
                      >
                        Not helpful
                      </Button>
                    </>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    aria-expanded={summaryExpanded}
                    aria-controls="executive-summary-details"
                    startIcon={summaryExpanded ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setSummaryExpanded((expanded) => !expanded)}
                    sx={insightCardToggleStyles}
                  >
                    {summaryExpanded ? "Show less" : "Show more"}
                  </Button>
                </Box>
              </Box>
              {!summaryExpanded && (
                <Box
                  sx={{
                    px: 2,
                    pb: 1.5,
                    display: "grid",
                    gridTemplateColumns: "32px minmax(0, 1fr)",
                    columnGap: 1.25,
                    bgcolor: "#F6FAFE",
                    background: "linear-gradient(90deg, #F7FBFF 0%, #EEF6FD 100%)"
                  }}
                >
                  <Typography
                    sx={{
                      gridColumn: 2,
                      minWidth: 0,
                      overflow: "hidden",
                      color: palette.muted,
                      fontSize: executiveSummaryItems.length > 0 ? 12 : 14,
                      lineHeight: 1.45,
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {executiveSummaryItems[0]?.text ?? "No executive summary is available for the selected period."}
                  </Typography>
                </Box>
              )}
              {summaryExpanded && (
                <Box
                  id="executive-summary-details"
                  sx={{ px: 2, py: 1.25, bgcolor: "#FFFFFF", borderTop: `1px solid ${palette.line}` }}
                >
                  {executiveSummaryItems.some(({ kind }) => kind === "heading") ? (
                    <ExecutiveSummarySections items={executiveSummaryItems} />
                  ) : executiveSummaryItems.length > 0 ? (
                    <Box sx={{ display: "grid", gap: 0.3 }}>
                      {executiveSummaryItems.map((item, index) => {
                        const currentStatus = item.text.match(/^(.*?)\b(current\s+status:)\s*(.+)$/i);
                        const summaryText = currentStatus ? currentStatus[1].trim() : item.text;
                        const labeledSummary =
                          item.level === 0 || item.kind === "bullet"
                            ? summaryText.match(/^([^:]+):\s*(.+)$/)
                            : null;
                        return (
                          <Box
                            key={`${index}-${item.text.slice(0, 24)}`}
                            sx={{
                              ml: item.level === 0 ? 0 : Math.min(item.level, 3) * (item.kind ? 2 : 4),
                              display: "grid",
                              gridTemplateColumns: item.level === 0 ? "minmax(0, 1fr)" : "20px minmax(0, 1fr)",
                              gap: item.level === 0 ? 0 : 0.75
                            }}
                          >
                            {item.level > 0 && (
                              <Box
                                aria-hidden="true"
                                sx={{
                                  mt: 0.75,
                                  width: 6,
                                  height: 6,
                                  justifySelf: "center",
                                  borderRadius: "50%",
                                  bgcolor: palette.blue
                                }}
                              />
                            )}
                            <Typography
                              component={
                                item.kind === "paragraph" || labeledSummary
                                  ? "p"
                                  : item.level === 0
                                    ? index === 0
                                      ? "h3"
                                      : "h4"
                                    : "p"
                              }
                              sx={{
                                m: 0,
                                mt: item.level === 0 && index > 0 ? 0.65 : 0,
                                color: item.kind || item.level === 0 ? palette.navy : palette.muted,
                                fontSize: item.level === 0 && index === 0 ? 15 : 14,
                                fontWeight:
                                  item.level === 0 && !labeledSummary && (item.kind !== "paragraph" || index === 0)
                                    ? 800
                                    : 400,
                                lineHeight: 1.65,
                                overflowWrap: "anywhere"
                              }}
                            >
                              {labeledSummary ? (
                                <>
                                  <Box component="span" sx={{ fontWeight: 800 }}>
                                    {labeledSummary[1]}:
                                  </Box>{" "}
                                  {labeledSummary[2]}
                                </>
                              ) : (
                                summaryText
                              )}
                              {currentStatus && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    mt: 0.75,
                                    mb: 1,
                                    color: palette.navy,
                                    fontSize: 13
                                  }}
                                >
                                  <Box component="strong" sx={{ fontWeight: 700 }}>
                                    {currentStatus[2]}
                                  </Box>
                                  {currentStatus[3].split("|").map((status, statusIndex) => {
                                    const statusText = status.trim();
                                    if (!statusText) return null;
                                    const statusParts = statusText.match(/^(.+):\s*(\d+)$/);
                                    const statusLabel = statusParts ? statusParts[1].trim() : statusText;
                                    const statusColor = /evidence gathering/i.test(statusLabel)
                                      ? palette.muted
                                      : /development/i.test(statusLabel)
                                        ? palette.blue
                                        : getStatusDistributionColor(statusLabel, 0);
                                    return (
                                      <Box
                                        component="span"
                                        key={`${statusIndex}-${statusLabel}`}
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                          px: 1,
                                          py: 0.5,
                                          maxWidth: "100%",
                                          minWidth: 0,
                                          border: `1px solid ${statusColor}40`,
                                          borderRadius: "16px",
                                          bgcolor: `${statusColor}12`,
                                          lineHeight: 1.5
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          aria-hidden="true"
                                          sx={{
                                            width: 7,
                                            height: 7,
                                            flexShrink: 0,
                                            borderRadius: "50%",
                                            bgcolor: statusColor
                                          }}
                                        />
                                        <Box component="span" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                                          {statusLabel}
                                        </Box>
                                        {statusParts && (
                                          <>
                                            {" "}
                                            <Box
                                              component="strong"
                                              sx={{
                                                px: 0.75,
                                                minWidth: 22,
                                                boxSizing: "border-box",
                                                borderRadius: "10px",
                                                bgcolor: "#FFFFFF",
                                                textAlign: "center",
                                                fontWeight: 800,
                                                flexShrink: 0
                                              }}
                                            >
                                              {statusParts[2]}
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography sx={{ ml: 5.25, color: palette.muted, fontSize: 14, lineHeight: 1.65 }}>
                      No executive summary is available for the selected period.
                    </Typography>
                  )}
                </Box>
              )}
              {summaryExpanded && feedback === "not-helpful" && (
                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    bgcolor: "#FFFFFF"
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="What was missing or incorrect?"
                    value={feedbackExplanation}
                    onChange={(event) => setFeedbackExplanation(event.target.value)}
                  />
                  <Button
                    variant="contained"
                    disabled={!feedbackExplanation.trim()}
                    onClick={() => {
                      setNotification("Thanks — your explanation was submitted for review.");
                      setFeedbackExplanation("");
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              )}
            </Paper>

            <Box
              sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                // gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.6fr) minmax(320px, 1fr)" },
                gap: 2
              }}
            >
              <Paper elevation={0} sx={{ ...surface, gridColumn: "1 / -1", p: 0, overflow: "hidden" }}>
                <Box sx={{ p: 2 }}>
                  <CardHeading
                    title="Top customer needs and adoption barriers"
                    subtitle="Correlated signal volume with customer and product breadth"
                    action={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: "100%", overflowX: "auto" }}>
                        <Typography sx={{ color: palette.muted, fontSize: 12, fontWeight: 800 }}>Source</Typography>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={selectedThemeSource}
                          aria-label="Signal source"
                          onChange={(_, sourceLabel) => {
                            if (sourceLabel !== null) setThemeSource(sourceLabel);
                          }}
                          sx={{
                            flexShrink: 0,
                            "& .MuiToggleButton-root": {
                              px: 1.5,
                              py: 0.5,
                              color: palette.muted,
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1.5,
                              textTransform: "none",
                              "&.Mui-selected": {
                                color: "#FFF",
                                bgcolor: palette.blue,
                                "&:hover": { bgcolor: palette.blue }
                              }
                            }
                          }}
                        >
                          {themeSources.map((sourceLabel) => (
                            <ToggleButton key={sourceLabel} value={sourceLabel} aria-label={sourceLabel}>
                              {sourceLabel}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    }
                  />
                </Box>
                <Divider />
                <Box
                  sx={{
                    p: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    bgcolor: "#FAFBFC"
                  }}
                >
                  <TextField
                    size="small"
                    value={needThemeSearch}
                    placeholder="Search needs or barriers..."
                    onChange={(event) => {
                      setNeedThemeSearch(event.target.value);
                      setShowAllNeedThemes(false);
                    }}
                    inputProps={{ "aria-label": "Search customer needs or adoption barriers" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: palette.muted, fontSize: 18 }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ width: { xs: "100%", sm: 310 }, "& .MuiInputBase-input": { fontSize: 13 } }}
                  />
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, ml: { sm: "auto" } }}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={needSignalStatus}
                      aria-label="Theme signal status"
                      onChange={(_, status: "open" | "all" | null) => {
                        if (status !== null) {
                          setNeedSignalStatus(status);
                          setShowAllNeedThemes(false);
                        }
                      }}
                      sx={{
                        gap: 1,
                        flexWrap: "wrap",
                        "& .MuiToggleButtonGroup-grouped": {
                          m: 0,
                          border: "1px solid #D8DEE8",
                          borderRadius: "4px",
                          bgcolor: "#FAFBFC",
                          px: 1.5,
                          py: 0,
                          gap: 1,
                          height: 34,
                          color: "#344563",
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1.5,
                          whiteSpace: "nowrap",
                          textTransform: "none",
                          "&:hover": { bgcolor: "#EEF2F7" },
                          "&.Mui-selected": {
                            color: "#FFFFFF",
                            bgcolor: "#0F5FC4",
                            borderColor: "#0F5FC4",
                            "&:hover": { bgcolor: "#0B52AD" }
                          },
                          "&.Mui-focusVisible": {
                            outline: `2px solid ${palette.navy}`,
                            outlineOffset: 2
                          }
                        }
                      }}
                    >
                      <ToggleButton value="open">
                        <DescriptionOutlined sx={{ fontSize: 20 }} />
                        Open signals
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            px: 1,
                            minWidth: 32,
                            borderRadius: "999px",
                            bgcolor: needSignalStatus === "open" ? "#FFFFFF" : "#DCE6F2",
                            color: palette.navy,
                            fontSize: 12,
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: "22px"
                          }}
                        >
                          {openNeedSignalCount}
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="all">
                        <DescriptionOutlined sx={{ fontSize: 20 }} />
                        All signals
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {["Open", "Closed"].map((status) => (
                      <Box key={status} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, bgcolor: getDashboardStatusColor(status), borderRadius: 0.5 }} />
                        <Typography sx={{ color: palette.muted, fontSize: 12 }}>{status}</Typography>
                      </Box>
                    ))}
                    <Typography sx={{ color: palette.muted, fontSize: 12 }}>
                      Showing {displayedNeedThemes.length} of {matchingNeedThemeCount}
                    </Typography>
                    <Select
                      size="small"
                      value={needThemeSort}
                      onChange={(event) =>
                        setNeedThemeSort(event.target.value as "volume-desc" | "volume-asc" | "name-asc")
                      }
                      inputProps={{ "aria-label": "Sort customer needs" }}
                      sx={{ minWidth: 175, height: 34, bgcolor: "#FFF", fontSize: 12 }}
                    >
                      <MenuItem value="volume-desc">Signal volume (high to low)</MenuItem>
                      <MenuItem value="volume-asc">Signal volume (low to high)</MenuItem>
                      <MenuItem value="name-asc">Name (A to Z)</MenuItem>
                    </Select>
                  </Box>
                </Box>
                <Divider />
                {displayedNeedThemeGroups.length > 0 ? (
                  <Stack spacing={1.25} sx={{ p: 1.25, bgcolor: "#FAFBFC" }}>
                    {displayedNeedThemeGroups.map(({ pillar, themes }) => {
                      const pillarStyle = needPillarStyles[pillar] ?? needPillarStyles.Other;
                      const PillarIcon = pillarStyle.icon;
                      const isExpanded = !collapsedNeedPillars.includes(pillar);

                      return (
                        <Box
                          key={pillar}
                          sx={{
                            overflow: "hidden",
                            border: `1px solid ${palette.line}`,
                            borderRadius: 1.25,
                            bgcolor: "#FFF",
                            boxShadow: "0 2px 8px rgba(9, 31, 58, 0.05)"
                          }}
                        >
                          <ButtonBase
                            aria-expanded={isExpanded}
                            aria-controls={`need-pillar-${pillar}`}
                            onClick={() =>
                              setCollapsedNeedPillars((collapsedPillars) =>
                                collapsedPillars.includes(pillar)
                                  ? collapsedPillars.filter((item) => item !== pillar)
                                  : [...collapsedPillars, pillar]
                              )
                            }
                            sx={{
                              width: "100%",
                              px: 1.5,
                              py: 0.9,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              textAlign: "left",
                              bgcolor: pillarStyle.background
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                borderRadius: "50%",
                                color: "#FFF",
                                bgcolor: pillarStyle.color
                              }}
                            >
                              <PillarIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography sx={{ color: palette.navy, fontSize: 13, fontWeight: 800 }}>
                              {pillar}
                            </Typography>
                            <Chip
                              label={`${themes.length} ${themes.length === 1 ? "theme" : "themes"}`}
                              size="small"
                              sx={{
                                height: 20,
                                color: palette.muted,
                                bgcolor: "rgba(255, 255, 255, 0.72)",
                                fontSize: 11,
                                fontWeight: 700
                              }}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Typography
                              sx={{
                                color: pillarStyle.color,
                                fontSize: 12,
                                fontWeight: 800,
                                whiteSpace: "nowrap"
                              }}
                            >
                              Total{" "}
                              {allNeedThemes
                                .filter((theme) => theme.pillar === pillar)
                                .reduce((total, theme) => total + theme.value, 0)}
                            </Typography>
                            {isExpanded ? (
                              <ExpandLess sx={{ color: pillarStyle.color, fontSize: 19 }} />
                            ) : (
                              <ExpandMore sx={{ color: pillarStyle.color, fontSize: 19 }} />
                            )}
                          </ButtonBase>
                          <Collapse in={isExpanded}>
                            <Stack id={`need-pillar-${pillar}`} divider={<Divider flexItem />}>
                              {themes.map((theme) => (
                                <ButtonBase
                                  key={`${pillar}-${theme.name}`}
                                  aria-label={`View contributing feedback for ${theme.name}`}
                                  onClick={() => {
                                    setContributingFeedbackTab("feedback");
                                    setExpandedNeedTheme({ name: theme.name, pillar });
                                  }}
                                  sx={{
                                    px: 1.5,
                                    py: 1,
                                    minHeight: 44,
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "minmax(0, 1fr) auto auto",
                                      sm: "minmax(220px, 0.55fr) minmax(120px, 1.45fr) auto auto"
                                    },
                                    alignItems: "center",
                                    columnGap: 1,
                                    textAlign: "left",
                                    transition: "background-color 120ms ease",
                                    "&:hover, &:focus-visible": { bgcolor: "#F3F7FC" },
                                    "&:focus-visible, &.Mui-focusVisible": {
                                      outline: `2px solid ${palette.blue}`,
                                      outlineOffset: -2
                                    }
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                                    <Typography noWrap title={theme.name} sx={{ fontSize: 12, fontWeight: 700 }}>
                                      {theme.name}
                                    </Typography>
                                    <Tooltip
                                      title={`Click this row to view contributing feedback and competitive assessment for ${theme.name}.`}
                                      arrow
                                      describeChild
                                    >
                                      <InfoOutlined
                                        tabIndex={0}
                                        role="img"
                                        aria-label={`About feedback for ${theme.name}`}
                                        sx={{
                                          color: palette.muted,
                                          fontSize: 16,
                                          flexShrink: 0,
                                          "&:focus-visible": {
                                            outline: `2px solid ${palette.blue}`,
                                            outlineOffset: 2,
                                            borderRadius: "50%"
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Box
                                    role="img"
                                    aria-label={
                                      theme.hasStatusData
                                        ? needSignalStatus === "open"
                                          ? `${theme.name}: Open ${theme.open}, Total ${theme.value}`
                                          : `${theme.name}: Open ${theme.open}, Closed ${theme.closed}, Total ${theme.value}`
                                        : `${theme.name}: Status counts not available, Total ${theme.value}`
                                    }
                                    title={
                                      theme.hasStatusData
                                        ? needSignalStatus === "open"
                                          ? `Open: ${theme.open}`
                                          : `Open: ${theme.open} | Closed: ${theme.closed} | Total: ${theme.value}`
                                        : "Status counts not available"
                                    }
                                    sx={{
                                      gridColumn: { xs: "1 / -1", sm: "auto" },
                                      gridRow: { xs: 2, sm: "auto" },
                                      mt: { xs: 0.4, sm: 0 },
                                      height: 18,
                                      display: "flex",
                                      bgcolor: "#EAF0F5",
                                      borderRadius: 0.5,
                                      overflow: "hidden"
                                    }}
                                  >
                                    {theme.hasStatusData ? (
                                      <>
                                        <Box
                                          sx={{
                                            width: `${(theme.open / maximumThemeValue) * 100}%`,
                                            height: "100%",
                                            bgcolor: getDashboardStatusColor("Open")
                                          }}
                                        />
                                        <Box
                                          sx={{
                                            width: `${(theme.closed / maximumThemeValue) * 100}%`,
                                            height: "100%",
                                            bgcolor: getDashboardStatusColor("Closed")
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <Typography sx={{ px: 0.5, color: palette.muted, fontSize: 11 }}>
                                        Status counts not available
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ textAlign: "right" }}>
                                    <Typography
                                      sx={{ color: palette.navy, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}
                                    >
                                      {theme.value} {needSignalStatus === "open" ? "open signals" : "signals"}
                                    </Typography>
                                    {theme.hasStatusData && (
                                      <Typography sx={{ color: palette.muted, fontSize: 11, whiteSpace: "nowrap" }}>
                                        {needSignalStatus === "open"
                                          ? `${theme.open} Open`
                                          : `${theme.open} Open | ${theme.closed} Closed`}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box
                                    component="span"
                                    aria-hidden="true"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.25,
                                      color: palette.blue,
                                      whiteSpace: "nowrap",
                                      fontSize: 12,
                                      fontWeight: 700
                                    }}
                                  >
                                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                                      View details
                                    </Box>
                                    <KeyboardArrowRight sx={{ fontSize: 20 }} />
                                  </Box>
                                </ButtonBase>
                              ))}
                            </Stack>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography sx={{ py: 4, textAlign: "center", color: palette.muted, fontSize: 11 }}>
                    {needSignalStatus === "open"
                      ? "No customer needs with open signals match your search. Select All signals to include closed signals and themes with unavailable status counts."
                      : "No customer needs match your search."}
                  </Typography>
                )}
                {matchingNeedThemeCount > 8 && (
                  <Box
                    sx={{ p: 1.25, display: "flex", justifyContent: "center", borderTop: `1px solid ${palette.line}` }}
                  >
                    <Button
                      size="small"
                      startIcon={showAllNeedThemes ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => setShowAllNeedThemes((expanded) => !expanded)}
                      sx={{ px: 2, textTransform: "none", fontSize: 10, fontWeight: 750 }}
                    >
                      {showAllNeedThemes ? "Show less" : `Show ${matchingNeedThemeCount - 8} more`}
                    </Button>
                  </Box>
                )}
              </Paper>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <CompetitiveAssessmentCard
                  id="competitive-assessment"
                  queryType="CompetitiveAssessment"
                  assessment={competitiveAssessment}
                  isFetching={competitiveAssessmentQuery.isFetching}
                  isError={competitiveAssessmentQuery.isError}
                  onRetry={() => competitiveAssessmentQuery.refetch()}
                  emptyMessage="No competitive assessment is available for the selected product."
                />
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <UXResearchInsightsCard
                  data={uxResearchInsightsQuery.data ?? []}
                  product={product}
                  isFetching={uxResearchInsightsQuery.isFetching}
                  isError={uxResearchInsightsQuery.isError}
                  onRetry={uxResearchInsightsQuery.refetch}
                />
              </Box>

              <Paper elevation={0} sx={{ ...surface }}>
                <Box sx={{ p: 2 }}>
                  <CardHeading
                    title={statusDistributionTitle}
                    subtitle="Current distribution across product and service workflows"
                  />
                </Box>
                <Divider />
                <Box
                  sx={{
                    mt: 2,
                    px: 2,
                    pb: 2,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: `repeat(${Math.max(1, Math.min(statusDistributions.length, 3))}, minmax(0, 1fr))`
                    },
                    gap: 2
                  }}
                >
                  {statusDistributions.map((distribution) => (
                    <StatusDistribution key={distribution.title} {...distribution} />
                  ))}
                </Box>
              </Paper>
              <NeedsAttentionPanel
                rows={needsAttentionRows}
                themeSummary={needsAttentionThemeSummary}
                isLoading={needsAttentionQuery.isLoading || needsAttentionQuery.isFetching}
                isError={needsAttentionQuery.isError}
                paginationKey={`${product}-${timeframe}`}
              />
              {/* <Paper elevation={0} sx={{ ...surface }}>
            <Box sx={{ p: 2 }}>
              <CardHeading
                title="Decision queue"
                subtitle="Highest-scoring intelligence awaiting accountable action"
                action={
                  <Chip
                    label="12 open"
                    size="small"
                    sx={{
                      height: 27,
                      borderRadius: 1,
                      bgcolor: "#FFF4CE",
                      color: "#8A5D00",
                      fontWeight: 750
                    }}
                  />
                }
              />
            </Box>
            <Divider />
            <Stack divider={<Divider flexItem />} sx={{ mt: 1, px: 2, pb: 2 }}>
              {decisions.map((decision) => (
                <Box key={decision.name} sx={{ py: 1.25, display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 750 }}>{decision.name}</Typography>
                    <Typography sx={{ mt: 0.3, color: palette.muted, fontSize: 9 }}>{decision.meta}</Typography>
                    <Box sx={{ mt: 0.7, display: "flex", gap: 0.35 }}>
                      {[1, 2, 3, 4, 5].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            width: 18,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: item <= Math.round(decision.score / 20) ? palette.blue : palette.line
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Avatar
                    variant="rounded"
                    sx={{
                      mt: 0.25,
                      width: 46,
                      height: 30,
                      borderRadius: 4,
                      bgcolor: palette.orange,
                      fontSize: 12,
                      fontWeight: 800
                    }}
                  >
                    {decision.score}
                  </Avatar>
                </Box>
              ))}
            </Stack>
          </Paper> */}
            </Box>
            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr" }, gap: 2 }}>
              <Paper elevation={0} sx={{ ...surface, mt: 2, overflow: "hidden" }}>
                <Box sx={{ p: 2 }}>
                  <CardHeading
                    title="Customer engagement intelligence"
                    subtitle="Customer deployment, product requests, service health, milestones, ownership, and recommended actions"
                    action={
                      <TextField
                        size="small"
                        placeholder="Search customer records"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                    }
                  />
                </Box>
                <TableContainer>
                  <Table size="small" aria-label="Customer engagement intelligence">
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: "#F7F9FB",
                          "& .MuiTableCell-root, & .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active":
                            {
                              color: "rgba(0, 0, 0, 0.87) !important",
                              WebkitTextFillColor: "rgba(0, 0, 0, 0.87) !important"
                            }
                        }}
                      >
                        {customerTableColumns.map(({ label, filterKey }) => (
                          <TableCell
                            key={filterKey}
                            sx={{
                              width: filterKey === "customer" ? 260 : "auto",
                              minWidth:
                                filterKey === "customer"
                                  ? 260
                                  : filterKey === "engagementSummary"
                                    ? 280
                                    : filterKey === "product" ||
                                        filterKey === "featureStatuses" ||
                                        filterKey === "icmStatuses" ||
                                        filterKey === "dcrStatuses" ||
                                        filterKey === "adoBugStatuses"
                                      ? 160
                                      : undefined,
                              fontSize: 11,
                              fontWeight: 800,
                              whiteSpace: "nowrap"
                            }}
                          >
                            <TableSortLabel
                              active={customerSortKey === filterKey}
                              direction={customerSortKey === filterKey ? customerSortDirection : "asc"}
                              onClick={() => handleCustomerSort(filterKey)}
                              sx={{ color: "inherit !important", fontSize: "inherit", fontWeight: "inherit" }}
                            >
                              {label}
                              {filterKey === "engagementSummary" && (
                                <Tooltip title="AI generated summary" arrow>
                                  <InfoOutlined sx={{ ml: 0.5, fontSize: 14, color: palette.muted }} />
                                </Tooltip>
                              )}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow sx={{ bgcolor: "#FAFBFC" }}>
                        {customerTableColumns.map(({ label, filterKey }) => (
                          <TableCell
                            key={filterKey}
                            sx={{
                              width: filterKey === "customer" ? 260 : "auto",
                              minWidth:
                                filterKey === "customer"
                                  ? 260
                                  : filterKey === "region" || filterKey === "segment"
                                    ? 180
                                    : filterKey === "engagementSummary"
                                      ? 280
                                      : filterKey === "product" ||
                                          filterKey === "featureStatuses" ||
                                          filterKey === "icmStatuses" ||
                                          filterKey === "dcrStatuses" ||
                                          filterKey === "adoBugStatuses"
                                        ? 160
                                        : undefined,
                              px: 0.75,
                              py: 0.6
                            }}
                          >
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Filter"
                              value={customerColumnFilters[filterKey]}
                              onChange={(event) =>
                                setCustomerColumnFilters((current) => ({
                                  ...current,
                                  [filterKey]: event.target.value
                                }))
                              }
                              inputProps={{ "aria-label": `Filter by ${label}` }}
                              sx={{
                                minWidth: 80,
                                "& .MuiInputBase-input": { px: 1, py: 0.65, fontSize: 12 }
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(customerEngagementDataQuery.isLoading || customerEngagementDataQuery.isFetching) && (
                        <TableRow>
                          <TableCell colSpan={customerTableColumns.length} align="center" sx={{ py: 4 }}>
                            <Stack sx={{ minHeight: 90, alignItems: "center", justifyContent: "center" }} spacing={1}>
                              <CircularProgress size={24} aria-label="Loading customer engagement data" />
                              <Typography sx={{ color: palette.muted, fontSize: 12 }}>
                                Loading customer engagement data...
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      {!customerEngagementDataQuery.isFetching && customerEngagementDataQuery.isError && (
                        <TableRow>
                          <TableCell colSpan={customerTableColumns.length} align="center" sx={{ py: 4 }}>
                            <Typography sx={{ color: palette.red, fontSize: 12 }}>
                              Customer engagement data could not be loaded.
                            </Typography>
                            <Button
                              size="small"
                              sx={{ mt: 1, textTransform: "none" }}
                              onClick={() => customerEngagementDataQuery.refetch()}
                            >
                              Retry
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                      {!customerEngagementDataQuery.isLoading &&
                        !customerEngagementDataQuery.isFetching &&
                        !customerEngagementDataQuery.isError &&
                        paginatedCustomers.map((customer) => (
                          <TableRow
                            key={`${customer.TenantId}-${customer.ProductName}-${customer.FeedbackPeriod}-${customer.SnapshotYearMonth}`}
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() => setSelectedCustomer(customer)}
                          >
                            <TableCell sx={{ width: 260, minWidth: 260 }}>
                              <Typography sx={{ color: palette.blue, fontSize: 12, fontWeight: 750 }}>
                                {customer.TenantName}
                              </Typography>
                              <Typography sx={{ color: palette.muted, fontSize: 12, whiteSpace: "nowrap" }}>
                                <Box component="span" sx={{ fontWeight: 750 }}>
                                  Tenant ID:
                                </Box>{" "}
                                {customer.TenantId}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 160, fontSize: 12 }}>{customer.ProductName}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {customer.CountryName ?? customer.CountryCode ?? "Not available"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{customer.RegionName}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{customer.SegmentName}</TableCell>
                            {isCustomerTablePurviewProduct ? (
                              <>
                                <TableCell sx={{ fontSize: 12 }}>{customer.DCRCount}</TableCell>
                                <TableCell sx={{ minWidth: 160, fontSize: 12 }}>
                                  {customer.DCRStatuses?.trim() || "Not available"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>{customer.ADOBugCount}</TableCell>
                                <TableCell sx={{ minWidth: 160, fontSize: 12 }}>
                                  {customer.ADOBugStatuses?.trim() || "Not available"}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell sx={{ fontSize: 12 }}>{customer.FeatureRequestCount}</TableCell>
                                <TableCell sx={{ minWidth: 160, fontSize: 12 }}>
                                  {customer.FeatureStatuses?.trim() || "Not available"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>{customer.ICMCount}</TableCell>
                                <TableCell sx={{ minWidth: 160, fontSize: 12 }}>
                                  {customer.ICMStatuses?.trim() || "Not available"}
                                </TableCell>
                              </>
                            )}
                            <TableCell sx={{ minWidth: 280, maxWidth: 360, fontSize: 12 }}>
                              <Tooltip title={customer.EngagementSummary ?? "Not available"} placement="top" arrow>
                                <Typography
                                  sx={{
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    fontSize: "inherit",
                                    lineHeight: 1.45,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 4
                                  }}
                                >
                                  {customer.EngagementSummary ?? "Not available"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      {!customerEngagementDataQuery.isLoading &&
                        !customerEngagementDataQuery.isFetching &&
                        !customerEngagementDataQuery.isError &&
                        !filteredCustomers.length && (
                          <TableRow>
                            <TableCell
                              colSpan={customerTableColumns.length}
                              align="center"
                              sx={{ py: 4, color: palette.muted }}
                            >
                              No customers match the selected filters.
                            </TableCell>
                          </TableRow>
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!customerEngagementDataQuery.isLoading &&
                  !customerEngagementDataQuery.isFetching &&
                  !customerEngagementDataQuery.isError &&
                  filteredCustomers.length > 0 && (
                    <TablePagination
                      component="div"
                      count={filteredCustomers.length}
                      page={customerPage}
                      rowsPerPage={customerRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25]}
                      onPageChange={(_event, nextPage) => setCustomerPage(nextPage)}
                      onRowsPerPageChange={(event) => {
                        setCustomerRowsPerPage(Number(event.target.value));
                        setCustomerPage(0);
                      }}
                      labelRowsPerPage="Rows per page"
                      sx={{
                        borderTop: `1px solid ${palette.line}`,
                        color: palette.muted,
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 }
                      }}
                    />
                  )}
              </Paper>
            </Box>
          </>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(expandedNeedTheme)}
        onClose={() => setExpandedNeedTheme(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "90vw", md: "85vw", lg: "80vw", xl: "75vw" },
            maxWidth: "100vw",
            display: "flex"
          }
        }}
      >
        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ color: palette.navy, fontSize: 18, fontWeight: 800 }}>
                Contributing feedback
              </Typography>
              <Typography
                noWrap
                title={expandedNeedTheme?.name ?? ""}
                sx={{ mt: 0.4, color: palette.muted, fontSize: 14 }}
              >
                {expandedNeedTheme?.name}
              </Typography>
            </Box>
            <IconButton aria-label="Close contributing feedback" onClick={() => setExpandedNeedTheme(null)}>
              <Close />
            </IconButton>
          </Box>
          <Tabs
            value={contributingFeedbackTab}
            onChange={(_event, value: "feedback" | "assessment") => setContributingFeedbackTab(value)}
            aria-label="Contributing feedback sections"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mt: 2, mb: 1.5, borderBottom: `1px solid ${palette.line}`, flexShrink: 0 }}
          >
            <Tab
              id="contributing-feedback-tab"
              aria-controls="contributing-feedback-panel"
              value="feedback"
              label="Feedback records"
              sx={{ textTransform: "none", fontSize: 14, fontWeight: 800 }}
            />
            <Tab
              id="theme-assessment-tab"
              aria-controls="theme-assessment-panel"
              value="assessment"
              label="Competitive Assessment"
              sx={{ textTransform: "none", fontSize: 14, fontWeight: 800 }}
            />
          </Tabs>
          <Box
            role="tabpanel"
            id="contributing-feedback-panel"
            aria-labelledby="contributing-feedback-tab"
            hidden={contributingFeedbackTab !== "feedback"}
            sx={{
              display: contributingFeedbackTab === "feedback" ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              minHeight: 0
            }}
          >
            <Box
              sx={{
                mb: 1.5,
                p: 1,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                bgcolor: "#F1F4F9",
                borderRadius: 1,
                flexShrink: 0
              }}
            >
              <Chip
                label={`${topCustomerNeedDrilldownRows.length} ${
                  topCustomerNeedDrilldownRows.length === 1 ? "record" : "records"
                } matching`}
                size="small"
                sx={{ height: 26, bgcolor: "#E8F1FF", color: palette.blue, fontSize: 12, fontWeight: 800 }}
              />
              <Typography sx={{ color: palette.muted, fontSize: 12 }}>
                Across {selectedThemeSource || "the selected source"} |{" "}
                {needSignalStatus === "open" ? "Open signals" : "All signals (open and closed)"}
              </Typography>
            </Box>
            <TableContainer sx={{ flex: 1, minHeight: 0, border: `1px solid ${palette.line}`, borderRadius: 1 }}>
              {topCustomerNeedsDrilldownQuery.isLoading || topCustomerNeedsDrilldownQuery.isFetching ? (
                <Stack sx={{ minHeight: 220, alignItems: "center", justifyContent: "center" }} spacing={1.25}>
                  <CircularProgress size={28} aria-label="Loading contributing feedback" />
                  <Typography sx={{ color: palette.muted, fontSize: 12 }}>Loading contributing feedback...</Typography>
                </Stack>
              ) : topCustomerNeedsDrilldownQuery.isError ? (
                <Stack sx={{ minHeight: 220, alignItems: "center", justifyContent: "center" }} spacing={1}>
                  <Typography sx={{ color: palette.red, fontSize: 12 }}>
                    Contributing feedback could not be loaded.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                    onClick={() => topCustomerNeedsDrilldownQuery.refetch()}
                  >
                    Retry
                  </Button>
                </Stack>
              ) : (
                <Table
                  stickyHeader
                  size="small"
                  aria-label={`Contributing feedback for ${expandedNeedTheme?.name ?? "selected need"}`}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-root, & .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active":
                          {
                            color: "rgba(0, 0, 0, 0.87) !important",
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.87) !important"
                          }
                      }}
                    >
                      {visibleContributingFeedbackColumns.map(({ label, sortKey: columnSortKey }) => (
                        <TableCell
                          key={columnSortKey}
                          sx={{
                            bgcolor: "#FAFBFC",
                            fontSize: 11,
                            fontWeight: 800,
                            whiteSpace: "nowrap"
                          }}
                        >
                          <TableSortLabel
                            active={activeContributingFeedbackSortKey === columnSortKey}
                            direction={
                              activeContributingFeedbackSortKey === columnSortKey
                                ? contributingFeedbackSortDirection
                                : "asc"
                            }
                            onClick={() => handleContributingFeedbackSort(columnSortKey)}
                            sx={{ color: "inherit !important", fontSize: "inherit", fontWeight: "inherit" }}
                          >
                            {label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow sx={{ bgcolor: "#FAFBFC" }}>
                      {visibleContributingFeedbackColumns.map(({ label, sortKey: columnFilterKey }) => (
                        <TableCell key={columnFilterKey} sx={{ px: 0.75, py: 0.6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Filter"
                            value={contributingFeedbackColumnFilters[columnFilterKey]}
                            onChange={(event) =>
                              setContributingFeedbackColumnFilters((current) => ({
                                ...current,
                                [columnFilterKey]: event.target.value
                              }))
                            }
                            inputProps={{ "aria-label": `Filter by ${label}` }}
                            sx={{
                              minWidth:
                                columnFilterKey === "feedbackDetails" || columnFilterKey === "customers" ? 180 : 100,
                              "& .MuiInputBase-input": { px: 1, py: 0.65, fontSize: 11 }
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTopCustomerNeedDrilldownRows.map((row) => (
                      <TableRow key={row.id ?? row.feedbackId} hover>
                        <TableCell sx={{ color: palette.blue, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {row.feedbackUrl?.trim() && row.feedbackId?.trim() ? (
                            <Typography
                              component="a"
                              href={row.feedbackUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.feedbackTitle ?? row.feedbackId}
                              sx={{
                                color: "inherit",
                                fontSize: "inherit",
                                fontWeight: "inherit",
                                textDecoration: "underline"
                              }}
                            >
                              {row.feedbackId}
                            </Typography>
                          ) : (
                            (row.feedbackId ?? "Not available")
                          )}
                        </TableCell>
                        {showFeedbackType && (
                          <TableCell sx={{ minWidth: 120, fontSize: 12 }}>
                            {row.feedbackTypeLabel ?? "Not available"}
                          </TableCell>
                        )}
                        <TableCell sx={{ minWidth: 190, fontSize: 12, wordBreak: "break-word" }}>
                          {row.feedbackTitle ?? "Not available"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 300, maxWidth: 420, fontSize: 12 }}>
                          <Tooltip title={row.feedbackDetails ?? "Not available"} placement="top" arrow>
                            <Typography
                              sx={{
                                display: "-webkit-box",
                                maxWidth: 400,
                                overflow: "hidden",
                                fontSize: 12,
                                lineHeight: 1.4,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 5
                              }}
                            >
                              {row.feedbackDetails ?? "Not available"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                          {row.customerCount ?? "Not available"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220, maxWidth: 320, fontSize: 12 }}>
                          <Tooltip title={row.customers ?? "Not available"} placement="top" arrow>
                            <Typography
                              sx={{
                                display: "-webkit-box",
                                maxWidth: 300,
                                overflow: "hidden",
                                fontSize: 12,
                                lineHeight: 1.4,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 5
                              }}
                            >
                              {row.customers ?? "Not available"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                          {row.feedbackStatus ?? "Not available"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!topCustomerNeedDrilldownRows.length && (
                      <TableRow>
                        <TableCell
                          colSpan={visibleContributingFeedbackColumns.length}
                          align="center"
                          sx={{ py: 4, color: palette.muted, fontSize: 12 }}
                        >
                          No contributing records match the selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            {!topCustomerNeedsDrilldownQuery.isLoading &&
              !topCustomerNeedsDrilldownQuery.isFetching &&
              !topCustomerNeedsDrilldownQuery.isError &&
              topCustomerNeedDrilldownRows.length > 0 && (
                <TablePagination
                  component="div"
                  count={topCustomerNeedDrilldownRows.length}
                  page={contributingFeedbackPage}
                  rowsPerPage={contributingFeedbackRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  onPageChange={(_event, nextPage) => setContributingFeedbackPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setContributingFeedbackRowsPerPage(Number(event.target.value));
                    setContributingFeedbackPage(0);
                  }}
                  labelRowsPerPage="Rows per page"
                  sx={{
                    borderTop: `1px solid ${palette.line}`,
                    color: palette.muted,
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 },
                    "& .MuiTablePagination-select": { fontSize: 12 }
                  }}
                />
              )}
          </Box>
          <Box
            role="tabpanel"
            id="theme-assessment-panel"
            aria-labelledby="theme-assessment-tab"
            hidden={contributingFeedbackTab !== "assessment"}
            sx={{
              display: contributingFeedbackTab === "assessment" ? "block" : "none",
              flex: 1,
              minHeight: 0,
              overflowY: "auto"
            }}
          >
            {expandedNeedTheme && (
              <CompetitiveAssessmentCard
                key={JSON.stringify([product, expandedNeedTheme.pillar, expandedNeedTheme.name])}
                id="theme-competitive-assessment"
                embedded
                queryType="TopCustomerNeedsCompetitiveAssessment"
                assessment={themeCompetitiveAssessment}
                isFetching={themeCompetitiveAssessmentQuery.isFetching}
                isError={themeCompetitiveAssessmentQuery.isError}
                onRetry={() => themeCompetitiveAssessmentQuery.refetch()}
                emptyMessage="No competitive assessment is available for the selected product, pillar, and theme."
              />
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={Boolean(selectedCustomer)}
        onClose={() => setSelectedCustomer(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 600, md: 720, lg: 840, xl: 960 },
            maxWidth: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
            overflow: "hidden"
          }
        }}
      >
        {selectedCustomer && (
          <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2.5, pb: 0, display: "flex", justifyContent: "space-between", gap: 2, flexShrink: 0 }}>
              <Box>
                <Typography component="h2" sx={{ color: palette.navy, fontSize: 21, fontWeight: 800 }}>
                  {selectedCustomer.TenantName}
                </Typography>
                <Typography sx={{ mt: 0.3, color: palette.muted, fontSize: 12, overflowWrap: "anywhere" }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    TPID:
                  </Box>{" "}
                  {selectedCustomer.TPID} ·{" "}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Tenant ID:
                  </Box>{" "}
                  {selectedCustomer.TenantId}
                </Typography>
              </Box>
              <IconButton aria-label="Close customer details" onClick={() => setSelectedCustomer(null)}>
                <Close />
              </IconButton>
            </Box>
            <Divider sx={{ mt: 2, flexShrink: 0 }} />
            <Stack spacing={2.2} sx={{ minHeight: 0, overflowY: "auto", p: 2.5, pt: 2, pb: 5 }}>
              <Box>
                <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 800 }}>Account overview</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                  {[
                    ["Segment", selectedCustomer.SegmentName],
                    ["Sub-segment", selectedCustomer.SubSegmentName],
                    ["Region", selectedCustomer.RegionName],
                    ["Country", selectedCustomer.CountryCode],
                    ["Industry", selectedCustomer.IndustryName],
                    ["Vertical", selectedCustomer.VerticalName]
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ p: 1, bgcolor: "#F7F9FB", borderRadius: 1 }}>
                      <Typography sx={{ color: palette.muted, fontSize: 12 }}>{label}</Typography>
                      <Typography sx={{ mt: 0.2, fontSize: 14, fontWeight: 750, overflowWrap: "anywhere" }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {selectedCustomerContacts.length > 0 && (
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 800 }}>Contact details</Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                      gap: 1
                    }}
                  >
                    {selectedCustomerContacts.map((contact, index) => (
                      <Box
                        key={`${contact}-${index}`}
                        sx={{
                          p: 1,
                          border: `1px solid ${palette.line}`,
                          borderRadius: 1,
                          bgcolor: "#FAFBFC"
                        }}
                      >
                        <Typography sx={{ fontSize: 14, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                          {contact}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              <Box>
                <Typography sx={{ mb: 0.7, fontSize: 16, fontWeight: 800 }}>Engagement summary</Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.6 }}>
                  {selectedCustomer.EngagementSummary?.trim() || "Not available"}
                </Typography>
              </Box>
              {selectedCustomerWorkItems.error && (
                <Box>
                  <Typography sx={{ mb: 0.7, fontSize: 16, fontWeight: 800 }}>Consolidated work items</Typography>
                  <Box sx={{ p: 1.25, border: `1px solid ${palette.red}`, borderRadius: 1, bgcolor: "#FFF4F4" }}>
                    <Typography sx={{ color: palette.red, fontSize: 14 }}>{selectedCustomerWorkItems.error}</Typography>
                  </Box>
                </Box>
              )}
              <Box>
                <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 800 }}>Customer signals overview</Typography>
                <Box
                  sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: 1 }}
                >
                  {[
                    ["Total", selectedCustomer.TotalFeedbacks],
                    ["Feature requests", selectedCustomer.FeatureRequestCount],
                    ["IcMs", selectedCustomer.ICMCount],
                    ["DCRs", selectedCustomer.DCRCount],
                    ["ADO bugs", selectedCustomer.ADOBugCount],
                    ["Survey responses", selectedCustomer.SurveyCount]
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ p: 1, textAlign: "center", bgcolor: "#F7F9FB", borderRadius: 1 }}>
                      <Typography sx={{ color: palette.muted, fontSize: 12 }}>{label}</Typography>
                      <Typography sx={{ mt: 0.2, color: palette.navy, fontSize: 18, fontWeight: 800 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {selectedCustomerWorkItemGroups.map(([source, workItems]) => (
                <Box key={source}>
                  <Typography
                    component="h3"
                    sx={{
                      mb: 1,
                      pb: 0.7,
                      borderBottom: `1px solid ${palette.line}`,
                      color: palette.navy,
                      fontSize: 16,
                      fontWeight: 800
                    }}
                  >
                    {getWorkItemGroupLabel(source)}
                  </Typography>
                  <Stack spacing={1}>
                    {workItems.map((workItem) => (
                      <Box
                        key={`${workItem.Source}-${workItem.Id}`}
                        sx={{
                          p: 1.25,
                          display: "grid",
                          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) auto" },
                          alignItems: "center",
                          gap: 1.5,
                          border: `1px solid ${palette.line}`,
                          borderRadius: 1,
                          bgcolor: "#FFFFFF"
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{ color: palette.navy, fontSize: 14, fontWeight: 800, overflowWrap: "anywhere" }}
                          >
                            {workItem.Title || "Untitled work item"}
                          </Typography>
                          <Typography sx={{ mt: 0.35, color: palette.muted, fontSize: 12, overflowWrap: "anywhere" }}>
                            {[workItem.Id, workItem.Status, workItem.Source].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                        {workItem.URL.trim() ? (
                          <Button
                            component="a"
                            href={workItem.URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{
                              px: 0.5,
                              minWidth: "auto",
                              whiteSpace: "nowrap",
                              fontSize: 12,
                              textTransform: "none"
                            }}
                          >
                            Open source ↗
                          </Button>
                        ) : (
                          <Typography sx={{ color: palette.muted, fontSize: 12, whiteSpace: "nowrap" }}>
                            Source unavailable
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
              <Box>
                <Typography sx={{ mb: 0.7, fontSize: 16, fontWeight: 800 }}>Signals status details</Typography>
                <Stack spacing={0.7}>
                  {[
                    ["Feature priorities", selectedCustomer.FeaturePriorities],
                    ["Feature statuses", selectedCustomer.FeatureStatuses],
                    ["IcM severities", selectedCustomer.ICMSeverities],
                    ["IcM statuses", selectedCustomer.ICMStatuses],
                    ["DCR statuses", selectedCustomer.DCRStatuses],
                    ["ADO bug statuses", selectedCustomer.ADOBugStatuses]
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ p: 1, border: `1px solid ${palette.line}`, borderRadius: 1 }}>
                      <Typography sx={{ color: palette.muted, fontSize: 12 }}>{label}</Typography>
                      <Typography sx={{ mt: 0.3, fontSize: 14 }}>{value?.trim() ? value : "Not available"}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: "#EEF6FC", borderLeft: `3px solid ${palette.blue}`, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>TOP THEMES</Typography>
                <Typography sx={{ mt: 0.5, fontSize: 14 }}>{selectedCustomer.TopThemes ?? "Not available"}</Typography>
                <Typography sx={{ mt: 0.7, color: palette.muted, fontSize: 12 }}>
                  First feedback: {formatFeedbackDate(selectedCustomer.FeedbackFirstCreatedDate)} · Last updated:{" "}
                  {formatFeedbackDate(selectedCustomer.FeedbackLastUpdatedDate)}
                </Typography>
              </Box>
              {/* <Button
                variant="contained"
                startIcon={<EmailOutlined />}
                onClick={() => setOutreachCustomer(selectedCustomer)}
              >
                Draft AI-assisted outreach
              </Button> */}
            </Stack>
          </Box>
        )}
      </Drawer>

      <Dialog
        open={askOpen}
        onClose={() => setAskOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: palette.navy,
            color: "#FFFFFF",
            px: 2.75,
            py: 2
          }}
        >
          <Box>
            <Typography component="h2" sx={{ fontSize: "inherit", fontWeight: 800, lineHeight: 1.25 }}>
              AI-assisted outreach
            </Typography>
            <Typography sx={{ mt: 0.4, color: "#D7E3F0", fontSize: 11 }}>Drafting for CVC-IQ · Ask CVC-IQ</Typography>
          </Box>
          <IconButton
            aria-label="Close AI-assisted outreach"
            onClick={() => setAskOpen(false)}
            sx={{
              mt: -0.25,
              mr: -0.5,
              color: "#FFFFFF",
              bgcolor: "rgba(255, 255, 255, 0.12)",
              borderRadius: 1,
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.75, pt: 2.25, pb: 1.5 }}>
          <Stack spacing={1.75}>
            <Box>
              <Typography sx={{ my: 0.6, color: palette.muted, fontSize: 11, fontWeight: 800 }}>TO</Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                value={askRecipient}
                onChange={(event) => setAskRecipient(event.target.value)}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 0.6, color: palette.muted, fontSize: 11, fontWeight: 800 }}>SUBJECT</Typography>
              <TextField
                fullWidth
                size="small"
                value={askSubject}
                onChange={(event) => setAskSubject(event.target.value)}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 0.6, color: palette.muted, fontSize: 11, fontWeight: 800 }}>
                AI-GENERATED DRAFT
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={8}
                value={askDraft}
                onChange={(event) => setAskDraft(event.target.value)}
                sx={{
                  "& textarea": {
                    resize: "vertical"
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.75, pb: 2.5, pt: 0.5, gap: 0.75 }}>
          <Button
            sx={{ textTransform: "none" }}
            variant="outlined"
            onClick={() => setAskDraft(createAskDraft(product))}
          >
            Regenerate
          </Button>
          <Button
            sx={{ textTransform: "none" }}
            variant="contained"
            disabled={!askRecipient.trim() || !askSubject.trim() || !askDraft.trim()}
            onClick={() => {
              setAskOpen(false);
              setNotification("Your message was sent to CVC-IQ.");
            }}
          >
            Send message
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={3500}
        message={notification}
        onClose={() => setNotification("")}
      />
    </Box>
  );
};

export default EngagementDashboard;
